import { PaymentAllocationModal } from "../components/domain/PaymentAllocationModal.js";
import { ArrowDownWideNarrow, ArrowUpNarrowWide, CalendarDays } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useModals } from "../components/common/ModalsProvider.js";
import { CHIP_OFF, FILTER_INPUT, FilterField } from "../components/common/FilterBar.js";
import { BankCard, BankShell, bankInputClass } from "../components/bank/Shell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useI18n, useT } from "../i18n/index.js";
import { BANK_KIND_LABEL_KEYS, bankLabelFallback, parseBankLabel } from "../lib/bank-label.js";
import { formatMoney, toNumber } from "../lib/format-fr.js";
import {
  BANK_DIRECT_REASON_KEYS,
  type BankDirectReasonKey,
  bankDirectReasonLabel,
  bankDirectReasonsFor,
} from "@eurostorm/shared";
import { trpc } from "../lib/trpc.js";
import { abrirArquivo } from "../lib/open-file.js";
import { Paginacao } from "../components/common/Paginacao.js";

const PAGE_SIZE = 50;

function buildCategoryLabels(t: ReturnType<typeof useT>): Record<string, string> {
  return {
    revenue: t("bankStatements.catRevenue"),
    supplier_payment: t("bankStatements.catSupplier"),
    tax: t("bankStatements.catTax"),
    salary: t("bankStatements.catSalary"),
    bank_fee: t("bankStatements.catBankFeeShort"),
    transfer: t("bankStatements.catTransfer"),
    other: t("bankStatements.catOther"),
    locations_charges_locatives: t("bankStatements.catRent"),
    primes_assurance: t("bankStatements.catInsurance"),
    deplacements_reception: t("bankStatements.catTravel"),
    fournitures_consommables: t("bankStatements.catSupplies"),
    intermediaires_honoraires: t("bankStatements.catFees"),
    vat_payment: t("bankStatements.catVatPayment"),
    personal: t("bankStatements.catPersonal"),
    partner_contribution: t("bankStatements.catPartnerContribution"),
    partner_reimbursement: t("bankStatements.catPartnerReimbursement"),
  };
}

const STATUS_TONE: Record<string, string> = {
  unreconciled: "bg-neutral-700/40 text-neutral-300",
  reconciled: "bg-lime/15 text-lime",
  ignored: "bg-neutral-600/40 text-neutral-400",
};

const CONFIDENCE_TONE: Record<string, string> = {
  unset: "text-neutral-600",
  auto: "text-sky-300",
  manual: "text-amber-300",
};

// F-releve-indy F1 — ponto colorido por FAMÍLIA de categoria (padrão Indy):
// verde = receita; esmeralda = fluxos do sócio; âmbar = impostos/TVA;
// laranja = salários; cinza = neutros; azul = demais charges.
const CATEGORY_DOT: Record<string, string> = {
  revenue: "bg-lime",
  partner_contribution: "bg-emerald-500",
  partner_reimbursement: "bg-emerald-500",
  personal: "bg-emerald-500",
  tax: "bg-amber-400",
  vat_payment: "bg-amber-400",
  salary: "bg-orange-400",
  transfer: "bg-neutral-500",
  other: "bg-neutral-500",
};

// Chaves dos meses reutilizadas do namespace monthlyEntryGrid (i18n global).
const MONTH_KEYS = [
  "monthlyEntryGrid.monthJanuary",
  "monthlyEntryGrid.monthFebruary",
  "monthlyEntryGrid.monthMarch",
  "monthlyEntryGrid.monthApril",
  "monthlyEntryGrid.monthMay",
  "monthlyEntryGrid.monthJune",
  "monthlyEntryGrid.monthJuly",
  "monthlyEntryGrid.monthAugust",
  "monthlyEntryGrid.monthSeptember",
  "monthlyEntryGrid.monthOctober",
  "monthlyEntryGrid.monthNovember",
  "monthlyEntryGrid.monthDecember",
] as const;

/**
 * F-releve-indy F1 — agrupa as rows da página (já ordenadas por data desc)
 * em meses → dias, com totais de entradas/saídas por mês (da página visível).
 */
function groupTimeline<T extends { transactionDate: string; amount: string }>(
  rows: ReadonlyArray<T>,
): Array<{
  key: string;
  year: number;
  month: number;
  inflow: number;
  outflow: number;
  days: Array<{ key: string; day: string; txs: T[] }>;
}> {
  const months: ReturnType<typeof groupTimeline<T>> = [];
  for (const tx of rows) {
    const mKey = tx.transactionDate.slice(0, 7);
    let mg = months[months.length - 1];
    if (!mg || mg.key !== mKey) {
      mg = {
        key: mKey,
        year: Number(tx.transactionDate.slice(0, 4)),
        month: Number(tx.transactionDate.slice(5, 7)),
        inflow: 0,
        outflow: 0,
        days: [],
      };
      months.push(mg);
    }
    const amount = Number(tx.amount) || 0;
    if (amount > 0) mg.inflow += amount;
    else mg.outflow += -amount;
    let dg = mg.days[mg.days.length - 1];
    if (!dg || dg.key !== tx.transactionDate) {
      dg = { key: tx.transactionDate, day: tx.transactionDate.slice(8, 10), txs: [] };
      mg.days.push(dg);
    }
    dg.txs.push(tx);
  }
  return months;
}

// F-extratos-month-filter — helpers do filtro por mês/ano (YYYY-MM).
function monthBounds(ym: string): { from: string; to: string } {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  const last = new Date(y, m, 0).getDate(); // dia 0 do mês seguinte = último do mês
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}` };
}

function shiftMonth(ym: string, delta: number): string {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function BankExtratos() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const modals = useModals();
  const t = useT();
  const { locale } = useI18n();
  const CATEGORY_LABELS = buildCategoryLabels(t);
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [category, setCategory] = useState<string>("all");
  // CEO 2026-11-03 — a notificação "N transações não conciliadas" abre com
  // ?status=unreconciled&periodo=todos: só as pendentes, de todas as datas.
  const urlInicial = new URLSearchParams(window.location.search);
  const [status, setStatus] = useState<string>(() => {
    const v = urlInicial.get("status");
    return v === "unreconciled" || v === "reconciled" || v === "ignored" ? v : "all";
  });
  const [ordem, setOrdem] = useState<"desc" | "asc">(() =>
    urlInicial.get("ordem") === "asc" ? "asc" : "desc",
  );
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  // F-extratos-month-filter — mês/ano navegável; dirige dateFrom/dateTo (reaproveita
  // o filtro de data do backend). Editar as datas manualmente limpa o mês.
  const [monthFilter, setMonthFilter] = useState("");
  function applyMonth(ym: string) {
    setMonthFilter(ym);
    setOffset(0);
    if (ym) {
      const b = monthBounds(ym);
      setDateFrom(b.from);
      setDateTo(b.to);
    }
  }

  // F-extrato-mes-atual (CEO 2026-09-27: "deve seguir o calendário atual") —
  // ao abrir (e ao trocar de empresa), mostra o MÊS CORRENTE. Antes abria no
  // último mês com conciliação concluída (agosto, em 27/09). O operador segue
  // livre para voltar com ◀, escolher outro mês ou digitar datas.
  const defaultMonthAppliedRef = useRef(false);
  useEffect(() => {
    defaultMonthAppliedRef.current = false;
  }, [companyId]);

  // F-recettes-encaissees Fase B — créditos sem categoria/conciliação no
  // período visível (mesma condição do balde "a triar" do fiscal).
  const untriagedQuery = trpc.bankTransactions.untriagedCredits.useQuery(
    {
      companyId: companyId ?? 0,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    },
    { enabled: companyId != null },
  );
  useEffect(() => {
    if (defaultMonthAppliedRef.current || companyId == null) return;
    defaultMonthAppliedRef.current = true;
    // ?periodo=todos (vindo da notificação): sem mês, todas as datas.
    if (new URLSearchParams(window.location.search).get("periodo") === "todos") return;
    const hoje = new Date();
    applyMonth(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`);
    // applyMonth é estável o suficiente para o escopo deste efeito de bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      bankAccountId: bankAccountId ?? undefined,
      // F-bank-direction-filter — "__credit"/"__debit" são pseudo-categorias
      // do seletor: filtram pelo SINAL do montante (todas as entradas /
      // todas as saídas), não por categoria contábil.
      category:
        category === "all" || category === "__credit" || category === "__debit"
          ? undefined
          : (category as CategoryValue),
      direction:
        category === "__credit"
          ? ("credit" as const)
          : category === "__debit"
            ? ("debit" as const)
            : undefined,
      reconciliationStatus:
        status === "all" ? undefined : (status as "unreconciled" | "reconciled" | "ignored"),
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      q: q || undefined,
      order: ordem,
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, bankAccountId, category, status, dateFrom, dateTo, q, ordem, offset],
  );

  const listQuery = trpc.bankTransactions.list.useQuery(filters, {
    enabled: companyId !== null,
  });
  const accountsQuery = trpc.bankAccounts.list.useQuery(
    { companyId: companyId ?? 0, limit: 50, offset: 0 },
    { enabled: companyId !== null },
  );

  const utils = trpc.useUtils();
  const ignoreMutation = trpc.bankTransactions.ignore.useMutation({
    onSuccess: () => utils.bankTransactions.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const unignoreMutation = trpc.bankTransactions.unignore.useMutation({
    onSuccess: () => utils.bankTransactions.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const unreconcileMutation = trpc.bankTransactions.unreconcile.useMutation({
    onSuccess: () => utils.bankTransactions.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  // F-releve-indy F2 — motor "Talvez?": sugestões de categoria (entidades
  // cadastradas + histórico) e de CONCILIAÇÃO (engine F-bank-drives-paid) sob
  // as transações pendentes.
  const pendingSugIds = useMemo(
    () =>
      (listQuery.data?.rows ?? [])
        .filter((r) => !r.category && r.reconciliationStatus === "unreconciled")
        .map((r) => r.id)
        .slice(0, 50),
    [listQuery.data],
  );
  const catSugsQuery = trpc.bankTransactions.categorySuggestions.useQuery(
    { companyId: companyId ?? 0, transactionIds: pendingSugIds },
    { enabled: companyId != null && pendingSugIds.length > 0 },
  );
  const reconSugsQuery = trpc.reconciliation.listSuggestions.useQuery(
    { companyId: companyId ?? 0, status: "pending", limit: 50, offset: 0 },
    { enabled: companyId != null },
  );
  const reconSugByTx = useMemo(() => {
    const m = new Map<number, NonNullable<typeof reconSugsQuery.data>["rows"][number]>();
    for (const s of reconSugsQuery.data?.rows ?? []) m.set(s.bankTransactionId, s);
    return m;
  }, [reconSugsQuery.data]);
  const acceptSugMutation = trpc.reconciliation.acceptSuggestion.useMutation({
    onSuccess: () => {
      utils.bankTransactions.invalidate();
      utils.reconciliation.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  // F-auto-categorize — "⚡ Reconhecer pendentes" (retroativo em massa).
  const autoRecognizeMutation = trpc.bankTransactions.autoRecognizePending.useMutation({
    onSuccess: (r) => {
      utils.bankTransactions.invalidate();
      void modals.alert({
        message: `⚡ ${r.categorized} ${t("bankStatements.autoRecognizeDoneMid")} ${r.scanned} ${t("bankStatements.autoRecognizeDoneSuf")}`,
      });
    },
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  // F-releve-indy F1 — categorização INLINE (dropdown no card, sem modal).
  const inlineCategorizeMutation = trpc.bankTransactions.updateCategory.useMutation({
    onSuccess: () => utils.bankTransactions.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const [openCatTx, setOpenCatTx] = useState<number | null>(null);
  // F4 — card expandido mostrando o libellé cru do banco.
  const [expandedTx, setExpandedTx] = useState<number | null>(null);
  const CATEGORY_OPTIONS_PAGE = buildCategoryOptions(t);

  const [reconcileModalTx, setReconcileModalTx] = useState<number | null>(null);
  const [categorizeModalTx, setCategorizeModalTx] = useState<number | null>(null);
  const [attachModalTx, setAttachModalTx] = useState<number | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [attachMonthOpen, setAttachMonthOpen] = useState(false);
  // F-conciliacao-passos (B3) — ações secundárias da barra no menu "⋯".
  const [moreOpen, setMoreOpen] = useState(false);

  // F-bank-tx-attachment — contagem de comprovantes por transação visível (1 query
  // batch p/ a página inteira → badge 📎 sem disparar N requisições).
  const visibleTxIds = useMemo(
    () => (listQuery.data?.rows ?? []).map((t) => t.id),
    [listQuery.data],
  );
  const attachCountsQuery = trpc.attachments.countByOwners.useQuery(
    { companyId: companyId ?? 0, ownerKind: "bank_transaction", ownerIds: visibleTxIds },
    { enabled: companyId !== null && visibleTxIds.length > 0 },
  );
  const attachCounts: Record<number, number> = attachCountsQuery.data ?? {};

  // F-statement-invoices — anexos que vivem nas FATURAS ligadas (depósitos
  // multi-fatura). Somados ao anexo direto p/ o badge 📎 refletir os documentos.
  const linkedAttachCountsQuery = trpc.bankTransactions.linkedInvoiceAttachCounts.useQuery(
    { companyId: companyId ?? 0, transactionIds: visibleTxIds },
    { enabled: companyId !== null && visibleTxIds.length > 0 },
  );
  const linkedAttachCounts: Record<number, number> = linkedAttachCountsQuery.data ?? {};
  // Fatura de fornecedor conciliada com PDF (fonte 3) → 1 documento.
  const purchaseDocCountsQuery = trpc.bankTransactions.purchaseDocCounts.useQuery(
    { companyId: companyId ?? 0, transactionIds: visibleTxIds },
    { enabled: companyId !== null && visibleTxIds.length > 0 },
  );
  const purchaseDocCounts: Record<number, number> = purchaseDocCountsQuery.data ?? {};
  const totalAttachCount = (txId: number) =>
    (attachCounts[txId] ?? 0) + (linkedAttachCounts[txId] ?? 0) + (purchaseDocCounts[txId] ?? 0);

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <BankShell subtitle={t("bankStatements.subtitleShort")}>
        <p className="text-sm text-neutral-400">{t("bankStatements.noCompanyAccess")}</p>
      </BankShell>
    );
  }

  const accounts = accountsQuery.data?.rows ?? [];
  const accountById = new Map(accounts.map((a) => [a.id, a]));
  // F-saldo-extrato — contas com saldo informado pelo último arquivo importado.
  const saldosExtrato = accounts.filter(
    (a) => a.statementBalance != null && a.statementBalanceAt != null,
  ) as Array<(typeof accounts)[number] & { statementBalance: string; statementBalanceAt: string }>;
  const dataBr = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  };
  const data = listQuery.data;
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toolbar = (
    <div className="flex items-end gap-x-3 gap-y-2 flex-wrap">
      <FilterField label={t("bankStatements.filterCompany")}>
        <select
          value={companyId ?? ""}
          onChange={(e) => {
            setCompanyId(Number(e.target.value));
            setBankAccountId(null);
            setOffset(0);
          }}
          className={FILTER_INPUT + " w-60"}
        >
          {accessibleCompanies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </FilterField>
      <FilterField label={t("bankStatements.filterAccount")}>
        <select
          value={bankAccountId ?? ""}
          onChange={(e) => {
            setBankAccountId(e.target.value === "" ? null : Number(e.target.value));
            setOffset(0);
          }}
          className={FILTER_INPUT + " w-48"}
        >
          <option value="">{t("bankStatements.allAccounts")}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.label} ({a.currency})
            </option>
          ))}
        </select>
      </FilterField>
      <FilterField label={t("bankStatements.filterCategory")}>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setOffset(0);
          }}
          className={FILTER_INPUT + " w-48"}
        >
          <option value="all">{t("bankStatements.allCategories")}</option>
          {/* Pseudo-categorias por direção — atalho pedido pelo operador para
              achar rapidamente tudo que entrou/saiu, sem depender da
              categorização contábil linha a linha. */}
          <option value="__credit">{t("bankStatements.allCredits")}</option>
          <option value="__debit">{t("bankStatements.allDebits")}</option>
          {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </FilterField>
      <FilterField label={t("bankStatements.filterStatus")}>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
          className={FILTER_INPUT + " w-36"}
        >
          <option value="all">{t("bankStatements.allStatuses")}</option>
          <option value="unreconciled">{t("bankStatements.statusPending")}</option>
          <option value="reconciled">{t("bankStatements.statusReconciled")}</option>
          <option value="ignored">{t("bankStatements.statusIgnored")}</option>
        </select>
      </FilterField>
      {/* F-extratos-month-filter — navegador de mês/ano (◀ mês ▶). Dirige as datas. */}
      <FilterField label={t("bankStatements.filterMonth")}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              if (monthFilter) applyMonth(shiftMonth(monthFilter, -1));
            }}
            disabled={!monthFilter}
            title={t("bankStatements.prevMonth")}
            className={CHIP_OFF + " !px-2.5 disabled:opacity-40"}
          >
            ◀
          </button>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => applyMonth(e.target.value)}
            className={FILTER_INPUT + " w-32"}
            title={t("bankStatements.filterByMonthTitle")}
          />
          <button
            type="button"
            onClick={() => {
              if (monthFilter) applyMonth(shiftMonth(monthFilter, 1));
            }}
            disabled={!monthFilter}
            title={t("bankStatements.nextMonth")}
            className={CHIP_OFF + " !px-2.5 disabled:opacity-40"}
          >
            ▶
          </button>
        </div>
      </FilterField>
      <FilterField label={t("bankStatements.filterPeriod")}>
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setMonthFilter("");
              setOffset(0);
            }}
            className={FILTER_INPUT + " w-36"}
          />
          <span style={{ color: "var(--text-muted)" }}>→</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setMonthFilter("");
              setOffset(0);
            }}
            className={FILTER_INPUT + " w-36"}
          />
        </div>
      </FilterField>
      <FilterField label={t("bankStatements.filterSearch")}>
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
          placeholder={t("bankStatements.searchPlaceholder")}
          className={FILTER_INPUT + " w-48"}
        />
      </FilterField>
      {/* F-conciliacao-passos (B3): "Enviar ao contador" é a ação principal;
          anexar faturas do mês e reconhecer pendentes ficam no "⋯". */}
      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={() => setExportOpen(true)}
          data-testid="send-accountant-btn"
          className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-lime px-3 text-sm font-semibold text-on-lime hover:bg-lime-bright"
          title={t("bankStatements.exportBtnTitle")}
        >
          📤 {t("bankStatements.sendToAccountantBtn")}
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            aria-label={t("bankStatements.moreActions")}
            aria-expanded={moreOpen}
            data-testid="bank-more-btn"
            className={CHIP_OFF + " !px-3"}
          >
            ⋯
          </button>
          {moreOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMoreOpen(false)} />
              <div
                className="absolute right-0 top-9 z-20 min-w-[240px] rounded-lg border border-neutral-700 bg-neutral-950 p-1 shadow-xl"
                data-testid="bank-more-menu"
              >
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false);
                    setAttachMonthOpen(true);
                  }}
                  disabled={!dateFrom || !dateTo}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-xs text-neutral-200 hover:bg-neutral-800 disabled:opacity-40"
                  title={t("bankStatements.attachMonthBtnTitle")}
                >
                  📎 {t("bankStatements.attachMonthBtn")}
                </button>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setMoreOpen(false);
                      autoRecognizeMutation.mutate({ companyId: companyId! });
                    }}
                    disabled={autoRecognizeMutation.isPending}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-xs text-neutral-200 hover:bg-neutral-800 disabled:opacity-40"
                    title={t("bankStatements.autoRecognizeTitle")}
                  >
                    ⚡{" "}
                    {autoRecognizeMutation.isPending
                      ? t("bankStatements.autoRecognizing")
                      : t("bankStatements.autoRecognizeBtn")}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <BankShell subtitle={t("bankStatements.subtitle")} toolbar={toolbar}>
      {/* F-recettes-encaissees Fase B — pendência de triagem: créditos sem
          categoria e sem conciliação no período visível NÃO entram nas
          recettes do fiscal até serem tratados aqui. */}
      {untriagedQuery.data && untriagedQuery.data.count > 0 && (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 flex items-center gap-3 flex-wrap">
          <span className="text-amber-500">⚠</span>
          {/* Cor via token do tema (não amber-100): legível no claro E no escuro. */}
          <span className="text-sm flex-1" style={{ color: "var(--text-strong)" }}>
            <strong>{untriagedQuery.data.count}</strong>{" "}
            {untriagedQuery.data.count === 1
              ? t("bankStatements.untriagedOneSuf")
              : t("bankStatements.untriagedManySuf")}{" "}
            — <strong>{formatMoney(Number(untriagedQuery.data.total), { decimals: 2 })}</strong>.{" "}
            {t("bankStatements.untriagedHint")}
          </span>
          <button
            onClick={() => {
              setCategory("__credit");
              setStatus("unreconciled");
              setOffset(0);
            }}
            className="rounded-md border border-amber-500/60 hover:border-amber-500 text-amber-600 px-3 py-1 text-xs font-medium"
          >
            {t("bankStatements.untriagedFilterBtn")}
          </button>
        </div>
      )}
      <BankCard
        title={`${total} ${total === 1 ? t("bankStatements.txCountOne") : t("bankStatements.txCountMany")}`}
        subtitle={`${t("bankStatements.pageLabel")} ${page} ${t("bankStatements.ofLabel")} ${totalPages}`}
        padded={false}
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* CEO 2026-11-03 — ordem da lista: mais recentes ou mais antigos primeiro. */}
            <button
              type="button"
              onClick={() => {
                setOrdem((o) => (o === "desc" ? "asc" : "desc"));
                setOffset(0);
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-700 px-3 py-1 text-xs hover:border-lime"
              title={t("bankStatements.sortToggleTitle")}
              data-testid="bank-sort-toggle"
            >
              {ordem === "desc" ? (
                <ArrowDownWideNarrow size={14} aria-hidden="true" />
              ) : (
                <ArrowUpNarrowWide size={14} aria-hidden="true" />
              )}
              {ordem === "desc" ? t("bankStatements.sortNewest") : t("bankStatements.sortOldest")}
            </button>
            {saldosExtrato.length > 0 ? (
              /* F-saldo-extrato (CEO 2026-10-25) — o saldo que o ARQUIVO importado
               informa, na data do extrato. Não é calculado. */
              <div className="flex flex-wrap justify-end gap-1.5" data-testid="statement-balances">
                {saldosExtrato.map((a) => (
                  <span
                    key={a.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-neutral-700 bg-neutral-900 px-3 py-1 text-xs"
                    title={t("bankStatements.statementBalanceTitle")}
                    data-testid={`statement-balance-${a.id}`}
                  >
                    <span className="text-neutral-400">
                      {a.label} · {t("bankStatements.statementBalancePre")}{" "}
                      {dataBr(a.statementBalanceAt)}
                    </span>
                    <b
                      className={`font-mono tabular-nums ${Number(a.statementBalance) >= 0 ? "text-lime" : "text-red-400"}`}
                    >
                      {formatMoney(a.statementBalance, { decimals: 2 })}
                    </b>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        }
      >
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("bankStatements.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("bankStatements.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && (
          <div className="p-3 sm:p-4 space-y-1">
            {/* F-releve-indy F1 — timeline por mês/dia (modelo Indy aprovado).
                A página já vem ordenada por data desc; aqui só agrupamos. */}
            {groupTimeline(data.rows).map((mg) => (
              <div key={mg.key}>
                <div className="flex items-center gap-2.5 mt-3 mb-2">
                  {/* CEO 2026-11-03 — calendário (era um emoji de tempestade, sem sentido aqui). */}
                  <div className="h-7 w-7 flex-none rounded-full bg-lime/10 border border-lime/40 flex items-center justify-center text-lime">
                    <CalendarDays size={14} aria-hidden="true" />
                  </div>
                  <h3 className="text-sm font-semibold text-neutral-100">
                    {t(MONTH_KEYS[mg.month - 1]!)} {mg.year}
                  </h3>
                  <span className="ml-auto text-[11px] text-neutral-500 tabular-nums">
                    {t("bankStatements.tlMonthIn")}{" "}
                    <b className="text-lime">+{formatMoney(mg.inflow, { decimals: 2 })}</b>
                    {" · "}
                    {t("bankStatements.tlMonthOut")}{" "}
                    <b className="text-red-400">−{formatMoney(mg.outflow, { decimals: 2 })}</b>
                  </span>
                </div>
                {mg.days.map((dg) => (
                  <div key={dg.key} className="flex gap-3 items-start">
                    <div className="w-10 flex-none text-center pt-2">
                      <div className="text-[15px] font-bold text-neutral-200 leading-none">
                        {dg.day}
                      </div>
                      <div className="text-[9.5px] uppercase text-neutral-500">
                        {t(MONTH_KEYS[mg.month - 1]!).slice(0, 3)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-2 border-l border-neutral-800 pl-3 pb-2.5">
                      {dg.txs.map((tx) => {
                        const account = accountById.get(tx.bankAccountId);
                        const amount = toNumber(tx.amount) ?? 0;
                        const attachN = totalAttachCount(tx.id);
                        const reconSug =
                          tx.reconciliationStatus === "unreconciled"
                            ? reconSugByTx.get(tx.id)
                            : undefined;
                        const catSugs =
                          !tx.category && tx.reconciliationStatus === "unreconciled"
                            ? (catSugsQuery.data?.[tx.id] ?? [])
                            : [];
                        return (
                          <div key={tx.id} className="space-y-1">
                            <div className="relative flex flex-wrap items-center gap-3 rounded-xl border border-neutral-800 bg-neutral-900 px-3.5 py-2.5 hover:border-neutral-700 transition-colors sm:flex-nowrap">
                              {/* clipe do justificativo — cheio quando tem anexo */}
                              <button
                                onClick={() => canEdit && setAttachModalTx(tx.id)}
                                title={t("bankStatements.attachBtnTitle")}
                                className={`h-8 w-8 flex-none rounded-lg border flex items-center justify-center text-sm ${
                                  attachN > 0
                                    ? "border-lime/40 bg-lime/10 text-lime"
                                    : "border-dashed border-neutral-700 text-neutral-500 hover:border-sky-500 hover:text-sky-500"
                                }`}
                              >
                                📎
                                {attachN > 0 ? (
                                  <span className="text-[9px] ml-0.5">{attachN}</span>
                                ) : null}
                              </button>
                              {/* F4 — libellé limpo (pedido do CEO comparando com a
                                  Indy): QUEM (contraparte após DE:/POUR:) em destaque,
                                  MOTIF (geralmente o nº da fatura) na 2ª linha; o texto
                                  cru do banco fica recolhido e expande ao clicar. */}
                              <div
                                className="flex-1 min-w-0 cursor-pointer select-none"
                                onClick={() => setExpandedTx(expandedTx === tx.id ? null : tx.id)}
                                title={t("bankStatements.expandHint")}
                              >
                                {(() => {
                                  const parsed = parseBankLabel(tx.description ?? "");
                                  const fallback = bankLabelFallback(tx.description);
                                  const kindLabel = parsed.kind
                                    ? t(BANK_KIND_LABEL_KEYS[parsed.kind])
                                    : null;
                                  return (
                                    <>
                                      <div className="text-[13px] font-semibold text-neutral-100 truncate">
                                        {parsed.counterparty ?? fallback}
                                      </div>
                                      <div className="text-[11px] text-neutral-500 truncate">
                                        {kindLabel && <span>{kindLabel}</span>}
                                        {parsed.motif && (
                                          <span className="text-neutral-300 font-medium">
                                            {kindLabel ? " · " : ""}
                                            {parsed.motif}
                                          </span>
                                        )}
                                        {!kindLabel && !parsed.motif && (
                                          <span>{account?.label ?? `#${tx.bankAccountId}`}</span>
                                        )}
                                      </div>
                                      {expandedTx === tx.id && (
                                        <div className="mt-1 text-[10.5px] text-neutral-500 font-mono whitespace-pre-wrap break-words border-t border-neutral-800 pt-1">
                                          {account?.label ?? `#${tx.bankAccountId}`} ·{" "}
                                          {tx.description ?? "—"}
                                          {tx.rawDescription && tx.rawDescription !== tx.description
                                            ? `\n${tx.rawDescription}`
                                            : ""}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              {/* F-extrato-celular (CEO 2026-10-25): abaixo de 640 px a linha
                                  quebra — descrição em cima; categoria, valor, selo e botões
                                  na 2ª linha. No computador `sm:contents` dissolve o
                                  invólucro e tudo fica numa linha só, como antes. */}
                              <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:contents">
                                {/* categoria inline: texto+ponto quando categorizada;
                                chip tracejado quando pendente; clique abre o dropdown */}
                                <div className="relative flex-none max-sm:mr-auto">
                                  <button
                                    onClick={() =>
                                      canEdit && setOpenCatTx(openCatTx === tx.id ? null : tx.id)
                                    }
                                    className={
                                      tx.category
                                        ? "flex items-center gap-1.5 text-xs font-semibold text-neutral-200 hover:text-lime"
                                        : "rounded-full border-[1.5px] border-dashed border-amber-500/60 text-amber-400 px-3 py-1 text-[11.5px] font-semibold hover:border-amber-400"
                                    }
                                    title={
                                      tx.category && tx.categoryConfidence === "auto"
                                        ? `${t("bankStatements.categorizeBtn")} · auto`
                                        : t("bankStatements.categorizeBtn")
                                    }
                                  >
                                    {tx.category ? (
                                      <>
                                        <span
                                          className={`h-2 w-2 rounded-full ${CATEGORY_DOT[tx.category] ?? "bg-sky-400"}`}
                                        />
                                        {CATEGORY_LABELS[tx.category] ?? tx.category}
                                        {tx.categoryConfidence === "auto" && (
                                          <span className="text-[9px] text-neutral-500">auto</span>
                                        )}
                                        {/* F4 — TVA padrão de serviços nos recebimentos de
                                          clientes (informativo; o fiscal usa a taxa real
                                          da fatura/cliente no classificador). */}
                                        {tx.category === "revenue" && amount > 0 && (
                                          <span className="text-[9.5px] text-neutral-500">
                                            TVA 20%
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <>{t("bankStatements.catPendingChip")}</>
                                    )}
                                    {canEdit && (
                                      <span className="text-[9px] opacity-50 ml-0.5">▾</span>
                                    )}
                                  </button>
                                  {openCatTx === tx.id && (
                                    <>
                                      <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setOpenCatTx(null)}
                                      />
                                      <div className="absolute right-0 top-8 z-20 min-w-[240px] max-h-72 overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-950 p-1 shadow-xl">
                                        {CATEGORY_OPTIONS_PAGE.map((opt) => (
                                          <button
                                            key={opt.value}
                                            onClick={() => {
                                              setOpenCatTx(null);
                                              inlineCategorizeMutation.mutate({
                                                companyId: companyId!,
                                                transactionId: tx.id,
                                                category:
                                                  opt.value as (typeof data.rows)[number]["category"] &
                                                    string,
                                              });
                                            }}
                                            className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-xs hover:bg-neutral-800 ${
                                              tx.category === opt.value
                                                ? "bg-lime/10 font-semibold text-lime"
                                                : "text-neutral-200"
                                            }`}
                                          >
                                            <span
                                              className={`h-2 w-2 flex-none rounded-full ${CATEGORY_DOT[opt.value] ?? "bg-sky-400"}`}
                                            />
                                            {opt.label}
                                          </button>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                                {/* valor + status */}
                                <span
                                  className={`w-[110px] flex-none text-right font-mono tabular-nums text-sm font-bold ${
                                    amount > 0
                                      ? "text-lime"
                                      : amount < 0
                                        ? "text-red-400"
                                        : "text-neutral-400"
                                  }`}
                                >
                                  {formatMoney(amount, { decimals: 2 })}
                                </span>
                                <span
                                  className={`w-[86px] flex-none text-center rounded px-2 py-0.5 text-[10.5px] ${STATUS_TONE[tx.reconciliationStatus]}`}
                                >
                                  {tx.reconciliationStatus === "unreconciled"
                                    ? t("bankStatements.badgePending")
                                    : tx.reconciliationStatus === "reconciled"
                                      ? t("bankStatements.badgeReconciled")
                                      : t("bankStatements.badgeIgnored")}
                                </span>
                                {tx.reconciledWithKind === "other" && tx.reconciliationReason && (
                                  <span
                                    className="hidden lg:inline max-w-[160px] truncate text-[10.5px] text-neutral-400"
                                    title={`${bankDirectReasonLabel(tx.reconciliationReason, locale)}${tx.reconciliationNotes ? ` · ${tx.reconciliationNotes}` : ""}`}
                                    data-testid="row-direct-reason"
                                  >
                                    {bankDirectReasonLabel(tx.reconciliationReason, locale)}
                                  </span>
                                )}
                                {canEdit && (
                                  <div className="flex justify-end gap-1">
                                    {tx.reconciliationStatus === "unreconciled" && (
                                      <>
                                        <button
                                          onClick={() => setReconcileModalTx(tx.id)}
                                          data-testid={`row-reconcile-${tx.id}`}
                                          className="rounded-md border border-lime/40 hover:border-lime text-lime px-2 py-0.5 text-xs font-semibold"
                                        >
                                          {t("bankStatements.reconcileBtn")}
                                        </button>
                                        <button
                                          onClick={async () => {
                                            const reason = await modals.promptReason({
                                              title: t("bankStatements.ignoreTitle"),
                                              message: t("bankStatements.ignoreMessage"),
                                              placeholder: t("bankStatements.ignorePlaceholder"),
                                              confirmLabel: t("bankStatements.ignoreConfirm"),
                                              required: true,
                                            });
                                            if (!reason) return;
                                            ignoreMutation.mutate({
                                              companyId: companyId!,
                                              transactionId: tx.id,
                                              reason,
                                            });
                                          }}
                                          className="rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-400 px-2 py-0.5 text-xs"
                                        >
                                          {t("bankStatements.ignoreBtn")}
                                        </button>
                                      </>
                                    )}
                                    {tx.reconciliationStatus === "reconciled" && (
                                      <button
                                        onClick={async () => {
                                          const reason = await modals.promptReason({
                                            title: t("bankStatements.unreconcileTitle"),
                                            message: t("bankStatements.unreconcileMessage"),
                                            placeholder: t("bankStatements.unreconcilePlaceholder"),
                                            confirmLabel: t("bankStatements.undoConfirm"),
                                            tone: "danger",
                                            required: true,
                                          });
                                          if (!reason) return;
                                          unreconcileMutation.mutate({
                                            companyId: companyId!,
                                            transactionId: tx.id,
                                            reason,
                                          });
                                        }}
                                        className="rounded-md border border-neutral-700 text-neutral-400 hover:border-red-500 hover:text-red-300 px-2 py-0.5 text-xs"
                                        data-testid="row-unreconcile"
                                      >
                                        {t("bankStatements.unreconcileBtn")}
                                      </button>
                                    )}
                                    {tx.reconciliationStatus === "ignored" && (
                                      <button
                                        onClick={async () => {
                                          const ok = await modals.confirm({
                                            title: t("bankStatements.unignoreTitle"),
                                            message: tx.reconciliationNotes
                                              ? `${t("bankStatements.reasonRecorded")} "${tx.reconciliationNotes}". ${t("bankStatements.unignoreMessage")}`
                                              : t("bankStatements.unignoreMessage"),
                                            confirmLabel: t("bankStatements.undoConfirm"),
                                            tone: "warning",
                                          });
                                          if (!ok) return;
                                          unignoreMutation.mutate({
                                            companyId: companyId!,
                                            transactionId: tx.id,
                                            reason: "Reverter via UI",
                                          });
                                        }}
                                        disabled={unignoreMutation.isPending}
                                        className="rounded-md border border-amber-500/40 hover:border-amber-500 text-amber-300 px-2 py-0.5 text-xs disabled:opacity-40"
                                      >
                                        {t("bankStatements.undoBtn")}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* F-releve-indy F2 — linha "Talvez?" sob a pendente */}
                            {canEdit && (reconSug || catSugs.length > 0) && (
                              <div className="flex items-center gap-2 flex-wrap pl-11">
                                <span className="rounded bg-lime/15 text-lime text-[9px] font-bold tracking-wider px-1.5 py-0.5">
                                  GESTION
                                </span>
                                <span className="text-[11px] text-neutral-500">
                                  {t("bankStatements.sugMaybe")}
                                </span>
                                {reconSug && (
                                  <button
                                    onClick={async () => {
                                      const reasons = Array.isArray(reconSug.matchReasons)
                                        ? (reconSug.matchReasons as string[]).join(" · ")
                                        : "";
                                      const ok = await modals.confirm({
                                        title: t("bankStatements.sugAcceptTitle"),
                                        message: `${t("bankStatements.sugAcceptMsgPre")} ${reasons || reconSug.suggestedKind}. ${t("bankStatements.sugAcceptMsgSuf")}`,
                                      });
                                      if (!ok) return;
                                      acceptSugMutation.mutate({
                                        companyId: companyId!,
                                        suggestionId: reconSug.id,
                                      });
                                    }}
                                    disabled={acceptSugMutation.isPending}
                                    className="rounded-full border border-lime/50 bg-lime/10 px-3 py-1 text-[11.5px] font-semibold text-lime hover:bg-lime/20 disabled:opacity-40"
                                    title={
                                      Array.isArray(reconSug.matchReasons)
                                        ? (reconSug.matchReasons as string[]).join(" · ")
                                        : undefined
                                    }
                                  >
                                    ✓ {t("bankStatements.sugReconcilePre")}{" "}
                                    {Array.isArray(reconSug.matchReasons) &&
                                    (reconSug.matchReasons as string[])[0]
                                      ? (reconSug.matchReasons as string[])[0]
                                      : reconSug.suggestedKind}
                                  </button>
                                )}
                                {catSugs.map((s, i) => (
                                  <button
                                    key={i}
                                    onClick={() =>
                                      inlineCategorizeMutation.mutate({
                                        companyId: companyId!,
                                        transactionId: tx.id,
                                        category: s.category as NonNullable<
                                          (typeof data.rows)[number]["category"]
                                        >,
                                      })
                                    }
                                    disabled={inlineCategorizeMutation.isPending}
                                    className="flex items-center gap-1.5 rounded-full border border-neutral-700 bg-neutral-900 px-3 py-1 text-[11.5px] text-neutral-200 hover:border-lime hover:text-lime disabled:opacity-40"
                                  >
                                    <span
                                      className={`h-1.5 w-1.5 rounded-full ${CATEGORY_DOT[s.category] ?? "bg-sky-400"}`}
                                    />
                                    {CATEGORY_LABELS[s.category] ?? s.category}
                                  </button>
                                ))}
                                {catSugs[0] && (
                                  <span className="text-[10px] text-neutral-600">
                                    {catSugs[0].reason.type === "entity"
                                      ? `${t("bankStatements.sugEntityPre")} ${catSugs[0].reason.name}`
                                      : `${t("bankStatements.sugHistoryPre")} ${catSugs[0].reason.count}× "${catSugs[0].reason.token}"`}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </BankCard>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          noun={total === 1 ? t("bankStatements.txCountOne") : t("bankStatements.txCountMany")}
          testId="bankExtratos-pagination"
        />
      )}

      {reconcileModalTx !== null && companyId !== null && (
        <ReconcileSheet
          companyId={companyId}
          transactionId={reconcileModalTx}
          onClose={() => setReconcileModalTx(null)}
          onSaved={() => {
            setReconcileModalTx(null);
            utils.bankTransactions.invalidate();
          }}
        />
      )}

      {categorizeModalTx !== null && companyId !== null && (
        <CategorizeModal
          companyId={companyId}
          transactionId={categorizeModalTx}
          onClose={() => setCategorizeModalTx(null)}
          onSaved={() => {
            setCategorizeModalTx(null);
            utils.bankTransactions.invalidate();
          }}
        />
      )}

      {attachModalTx !== null && companyId !== null && (
        <TxAttachmentsModal
          companyId={companyId}
          transactionId={attachModalTx}
          onClose={() => setAttachModalTx(null)}
          onChanged={() => utils.attachments.countByOwners.invalidate()}
        />
      )}

      {exportOpen && companyId !== null && (
        <StatementExportModal
          companyId={companyId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={() => setExportOpen(false)}
        />
      )}

      {attachMonthOpen && companyId !== null && dateFrom && dateTo && (
        <MonthInvoicesAttachModal
          companyId={companyId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={() => setAttachMonthOpen(false)}
        />
      )}
    </BankShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// F-bank-tx-attachment — Modal: anexar/listar comprovantes de uma transação.
// Permite anexar arquivo a QUALQUER conciliação (não só fatura cliente/forn.) —
// o comprovante vira o link clicável da linha no PDF do extrato.
// ────────────────────────────────────────────────────────────────────────────

function TxAttachmentsModal({
  companyId,
  transactionId,
  onClose,
  onChanged,
}: {
  companyId: number;
  transactionId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const modals = useModals();
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const t = useT();
  const isAdmin =
    user?.isSuperAdmin === true ||
    (user?.memberships ?? []).some(
      (m) => m.companyId === companyId && m.active && m.role === "admin",
    );

  const txQuery = trpc.bankTransactions.getById.useQuery({ companyId, transactionId });
  const listQuery = trpc.attachments.listByOwner.useQuery({
    companyId,
    ownerKind: "bank_transaction",
    ownerId: transactionId,
  });
  const deleteMut = trpc.attachments.delete.useMutation();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tx = txQuery.data;
  const items = listQuery.data ?? [];

  // F-statement-invoices — faturas ligadas a esta transação (para anexar os
  // PDFs do eDSP direto na linha, casando pelo número do arquivo).
  const linkedQuery = trpc.bankTransactions.linkedInvoices.useQuery({
    companyId,
    transactionId,
  });
  const linkedInvoices = linkedQuery.data ?? [];
  // Fatura de fornecedor conciliada (fonte 3 do link do extrato).
  const purchaseDoc = trpc.bankTransactions.reconciledPurchaseDoc.useQuery({
    companyId,
    transactionId,
  }).data;
  // F-bank-proforma-doc — saída conciliada a uma PROFORMA: a fatura recebida
  // anexada na proforma é o justificativo desta linha (e o link do extrato).
  const proformaDoc = trpc.bankTransactions.reconciledProformaDoc.useQuery({
    companyId,
    transactionId,
  }).data;
  const [invBusy, setInvBusy] = useState(false);
  const [invMsg, setInvMsg] = useState<string | null>(null);

  function refresh() {
    void listQuery.refetch();
    void utils.attachments.invalidate();
    onChanged();
  }

  /**
   * Anexa vários PDFs às faturas DESTE lançamento, casando o nome do arquivo
   * (sem extensão) com o número da fatura. Cada arquivo vira anexo da fatura
   * (ownerKind='customer_invoice') → aparece como link no extrato.
   */
  async function attachToInvoices(fileList: FileList | null) {
    const files = (fileList ? Array.from(fileList) : []).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
    );
    if (files.length === 0) return;
    const byNum = new Map(linkedInvoices.map((i) => [i.number.trim().toUpperCase(), i.invoiceId]));
    const fileNum = (f: File) =>
      f.name
        .replace(/\.[^.]+$/, "")
        .replace(/\s*\(\d+\)\s*$/, "")
        .trim()
        .toUpperCase();
    setInvBusy(true);
    let ok = 0;
    const unmatched: string[] = [];
    try {
      let i = 0;
      for (const f of files) {
        i += 1;
        const id = byNum.get(fileNum(f));
        if (id == null) {
          unmatched.push(f.name);
          continue;
        }
        setInvMsg(`${t("bankStatements.sending")} ${i}/${files.length}…`);
        const fd = new FormData();
        fd.append("file", f);
        fd.append("companyId", String(companyId));
        fd.append("ownerId", String(id));
        fd.append("ownerKind", "customer_invoice");
        const resp = await fetch("/uploads", { method: "POST", credentials: "include", body: fd });
        if (resp.ok) ok += 1;
        else unmatched.push(`${f.name} (${t("bankStatements.failed")})`);
      }
      await linkedQuery.refetch();
      void utils.attachments.invalidate();
      onChanged();
      void modals.alert({
        title: t("bankStatements.linkedInvoicesAlertTitle"),
        message:
          `✅ ${ok} ${t("bankStatements.pdfsAttachedToInvoices")}` +
          (unmatched.length
            ? `\n\n⚠️ ${unmatched.length} ${t("bankStatements.noMatchingInvoiceInEntry")}: ${unmatched.slice(0, 8).join(", ")}${unmatched.length > 8 ? "…" : ""}`
            : ""),
        tone: unmatched.length ? "warning" : "default",
      });
    } finally {
      setInvBusy(false);
      setInvMsg(null);
    }
  }

  async function handleUpload(fileList: FileList | null) {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("companyId", String(companyId));
        fd.append("ownerId", String(transactionId));
        fd.append("ownerKind", "bank_transaction");
        const resp = await fetch("/uploads", { method: "POST", credentials: "include", body: fd });
        if (!resp.ok) {
          const body = (await resp.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? t("bankStatements.uploadFailed"));
        }
      }
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function openAttachment(attachmentId: number) {
    try {
      await abrirArquivo(
        async () => (await utils.attachments.getDownloadUrl.fetch({ companyId, attachmentId })).url,
      );
    } catch (e) {
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    }
  }

  async function removeAttachment(attachmentId: number, filename: string) {
    const ok = await modals.confirm({
      title: t("bankStatements.removeReceiptTitle"),
      message: `${t("bankStatements.removeQuestionPre")} "${filename}"? ${t("bankStatements.cannotBeUndone")}`,
      confirmLabel: t("bankStatements.removeConfirm"),
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteMut.mutateAsync({ companyId, attachmentId });
      refresh();
    } catch (e) {
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-lg bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("bankStatements.txAttachmentsTitle")}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-4">
          {tx && (
            <div className="bg-neutral-800/30 rounded-md px-3 py-2 text-sm">
              <div className="font-mono text-neutral-200">
                {tx.transactionDate} ·{" "}
                <span className={Number(tx.amount) > 0 ? "text-lime" : "text-red-400"}>
                  {formatMoney(tx.amount, { decimals: 2 })}
                </span>
              </div>
              <div className="text-neutral-300 truncate" title={tx.description ?? ""}>
                {tx.description ?? "—"}
              </div>
            </div>
          )}

          {/* F-bank-proforma-doc — proforma conciliada: a fatura recebida (BS /
              técnico) anexada nela é o justificativo e o link do extrato. */}
          {proformaDoc && (
            <div className="rounded-md border border-lime/30 bg-lime/5 px-3 py-2.5">
              <div className="text-xs font-semibold text-lime mb-1">
                {t("bankStatements.proformaDocReconciled")}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-mono text-neutral-200">
                  {proformaDoc.invoiceNumber ?? "—"}
                  <span className="ml-2 text-xs text-neutral-500">
                    ·{" "}
                    <a
                      href={`/proformas/${proformaDoc.proformaRequestId}`}
                      className="hover:underline"
                    >
                      {proformaDoc.reference}
                    </a>
                    {proformaDoc.recipientName ? ` · ${proformaDoc.recipientName}` : ""}
                  </span>
                </span>
                {proformaDoc.attachmentId != null ? (
                  <button
                    type="button"
                    onClick={() => openAttachment(proformaDoc.attachmentId!)}
                    className="text-xs text-sky-300 hover:underline"
                    title={t("bankStatements.openProformaInvoicePdfTitle")}
                  >
                    📄 {t("bankStatements.openPdf")}
                  </button>
                ) : (
                  <span className="text-xs text-amber-400">
                    ⚠ {t("bankStatements.proformaNoInvoicePdf")}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">
                {t("bankStatements.accountantLinkNote")}
              </p>
            </div>
          )}

          {/* F-statement-invoices — fatura de FORNECEDOR conciliada: o PDF vive no
              registro da fatura (Compras) e é o link que o extrato usa nesta linha. */}
          {purchaseDoc && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
              <div className="text-xs font-semibold text-amber-300 mb-1">
                {t("bankStatements.purchaseInvoiceReconciled")}
              </div>
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="font-mono text-neutral-200">{purchaseDoc.number}</span>
                {purchaseDoc.attachmentId != null ? (
                  <button
                    type="button"
                    onClick={() => openAttachment(purchaseDoc.attachmentId!)}
                    className="text-xs text-sky-300 hover:underline"
                    title={t("bankStatements.openPurchasePdfTitle")}
                  >
                    📄 {t("bankStatements.openPdf")}
                  </button>
                ) : (
                  <span className="text-xs text-amber-400">⚠ {t("bankStatements.noPdf")}</span>
                )}
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">
                {t("bankStatements.accountantLinkNote")}
              </p>
            </div>
          )}

          {/* F-statement-invoices — faturas ligadas a este lançamento: anexe os PDFs
              do eDSP (casando pelo nº) → viram links no extrato do contador. */}
          {linkedInvoices.length > 0 && (
            <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2.5 space-y-2">
              <div className="text-xs font-semibold text-sky-300">
                {t("bankStatements.invoicesOfEntry")} ({linkedInvoices.length})
              </div>
              <ul className="space-y-1">
                {linkedInvoices.map((inv) => (
                  <li
                    key={inv.invoiceId}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="font-mono text-neutral-200">{inv.number}</span>
                    {inv.attachmentId != null ? (
                      <button
                        type="button"
                        onClick={() => openAttachment(inv.attachmentId!)}
                        className="text-xs text-sky-300 hover:underline"
                        title={t("bankStatements.openInvoicePdfTitle")}
                      >
                        📄 {t("bankStatements.openPdf")}
                      </button>
                    ) : (
                      <span className="text-xs text-amber-400">⚠ {t("bankStatements.noPdf")}</span>
                    )}
                  </li>
                ))}
              </ul>
              <label
                className={`block rounded-md border border-dashed px-3 py-3 text-center text-xs cursor-pointer transition-colors ${
                  invBusy
                    ? "border-neutral-800 text-neutral-600"
                    : "border-sky-500/50 text-sky-300 hover:border-sky-400 hover:text-sky-200"
                }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!invBusy) void attachToInvoices(e.dataTransfer.files);
                }}
              >
                {invBusy ? (
                  (invMsg ?? t("bankStatements.sendingEllipsis"))
                ) : (
                  <>
                    <span className="block font-medium">
                      + {t("bankStatements.attachEdspPdfs")}
                    </span>
                    <span className="block text-[11px] text-neutral-500 mt-0.5">
                      {t("bankStatements.selectPdfsPre")} {linkedInvoices.length}{" "}
                      {t("bankStatements.selectPdfsMid")} {linkedInvoices[0]?.number}
                      {t("bankStatements.selectPdfsSuf")}
                    </span>
                  </>
                )}
                <input
                  type="file"
                  accept="application/pdf"
                  multiple
                  className="hidden"
                  disabled={invBusy}
                  onChange={(e) => {
                    void attachToInvoices(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
            </div>
          )}

          <p className="text-xs text-neutral-400">{t("bankStatements.attachHelp")}</p>

          {items.length === 0 ? (
            <p className="text-sm text-neutral-600 italic">{t("bankStatements.noAttachments")}</p>
          ) : (
            <ul className="space-y-1.5">
              {items.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-neutral-800 px-3 py-2"
                >
                  <button
                    onClick={() => openAttachment(a.id)}
                    className="text-left text-sm text-sky-300 hover:underline truncate"
                    title={a.originalFilename}
                  >
                    📄 {a.originalFilename}
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.sizeBytes != null && (
                      <span className="text-[10px] text-neutral-600">
                        {Math.max(1, Math.round(a.sizeBytes / 1024))} KB
                      </span>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => removeAttachment(a.id, a.originalFilename)}
                        className="text-xs text-red-400 hover:text-red-300"
                        title={t("bankStatements.removeTitle")}
                      >
                        {t("bankStatements.removeBtn")}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}

          <label
            className={`block rounded-md border border-dashed px-4 py-6 text-center text-sm cursor-pointer transition-colors ${
              busy
                ? "border-neutral-800 text-neutral-600"
                : "border-neutral-700 text-neutral-300 hover:border-lime hover:text-lime"
            }`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (!busy) void handleUpload(e.dataTransfer.files);
            }}
          >
            {busy ? (
              t("bankStatements.sendingEllipsis")
            ) : (
              <>
                <span className="block font-medium">+ {t("bankStatements.attachFiles")}</span>
                <span className="block text-[11px] text-neutral-500 mt-1">
                  {t("bankStatements.clickToChooseOr")}{" "}
                  <strong>{t("bankStatements.dragFilesHere")}</strong> ·{" "}
                  {t("bankStatements.maxSizeEach")}
                </span>
              </>
            )}
            <input
              type="file"
              multiple
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                const fl = e.target.files;
                void handleUpload(fl);
                e.currentTarget.value = "";
              }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// F-bank-statement-export (M3) — Modal: exportar extrato (PDF+CSV) e enviar ao contador
// ────────────────────────────────────────────────────────────────────────────

/** Lê um File como base64 puro (sem o prefixo `data:...;base64,`). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

interface OriginalStatement {
  filename: string;
  contentBase64: string;
  contentType: string;
}

/**
 * F-statement-invoices — modal "Anexar faturas do mês": lista as faturas
 * emitidas (issued/paid) no período do extrato com status ✓/⚠ PDF e um
 * drop-zone que casa os PDFs (do eDSP) pelo nº do arquivo e anexa a cada
 * fatura (ownerKind='customer_invoice'). Depois disso, o extrato mostra os
 * links de download de cada fatura.
 */
function MonthInvoicesAttachModal({
  companyId,
  dateFrom,
  dateTo,
  onClose,
}: {
  companyId: number;
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
}) {
  const modals = useModals();
  const utils = trpc.useUtils();
  const t = useT();
  const listQuery = trpc.customerInvoices.issuedInPeriod.useQuery({ companyId, dateFrom, dateTo });
  const invoices = listQuery.data ?? [];
  const missing = invoices.filter((i) => !i.hasPdf).length;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function openAttachment(attachmentId: number) {
    try {
      await abrirArquivo(
        async () => (await utils.attachments.getDownloadUrl.fetch({ companyId, attachmentId })).url,
      );
    } catch (e) {
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    }
  }

  async function attachFiles(fileList: FileList | null) {
    const files = (fileList ? Array.from(fileList) : []).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
    );
    if (files.length === 0) return;
    const byNum = new Map(invoices.map((i) => [i.number.trim().toUpperCase(), i.invoiceId]));
    const fileNum = (f: File) =>
      f.name
        .replace(/\.[^.]+$/, "")
        .replace(/\s*\(\d+\)\s*$/, "")
        .trim()
        .toUpperCase();
    setBusy(true);
    let ok = 0;
    const unmatched: string[] = [];
    try {
      let i = 0;
      for (const f of files) {
        i += 1;
        const id = byNum.get(fileNum(f));
        if (id == null) {
          unmatched.push(f.name);
          continue;
        }
        setMsg(`${t("bankStatements.sending")} ${i}/${files.length}…`);
        const fd = new FormData();
        fd.append("file", f);
        fd.append("companyId", String(companyId));
        fd.append("ownerId", String(id));
        fd.append("ownerKind", "customer_invoice");
        const resp = await fetch("/uploads", { method: "POST", credentials: "include", body: fd });
        if (resp.ok) ok += 1;
        else unmatched.push(`${f.name} (${t("bankStatements.failed")})`);
      }
      await listQuery.refetch();
      void utils.attachments.invalidate();
      void modals.alert({
        title: t("bankStatements.monthAttachAlertTitle"),
        message:
          `✅ ${ok} ${t("bankStatements.pdfsAttached")}` +
          (unmatched.length
            ? `\n\n⚠️ ${unmatched.length} ${t("bankStatements.noMatchingInvoiceInPeriod")}: ${unmatched.slice(0, 8).join(", ")}${unmatched.length > 8 ? "…" : ""}`
            : ""),
        tone: unmatched.length ? "warning" : "default",
      });
    } finally {
      setBusy(false);
      setMsg(null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-lg bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("bankStatements.monthInvoicesTitle")}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-neutral-400">
            {dateFrom} {t("bankStatements.toDate")} {dateTo}.{" "}
            {t("bankStatements.monthInvoicesHelp")}
          </p>

          {listQuery.isLoading ? (
            <p className="text-sm text-neutral-500">{t("bankStatements.loading")}</p>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-neutral-600 italic">
              {t("bankStatements.noInvoicesInPeriod")}
            </p>
          ) : (
            <>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-neutral-300">
                  {invoices.length} {t("bankStatements.invoicesUnit")}
                </span>
                <span className="text-lime">
                  ✓ {invoices.length - missing} {t("bankStatements.withPdf")}
                </span>
                {missing > 0 && (
                  <span className="text-amber-400">
                    ⚠ {missing} {t("bankStatements.noPdf")}
                  </span>
                )}
              </div>
              <ul className="space-y-1 max-h-64 overflow-y-auto rounded-md border border-neutral-800 p-2">
                {invoices.map((inv) => (
                  <li
                    key={inv.invoiceId}
                    className="flex items-center justify-between gap-2 text-sm px-1"
                  >
                    <span className="font-mono text-neutral-200">{inv.number}</span>
                    <span className="flex items-center gap-3">
                      <span className="text-neutral-400 tabular-nums">
                        {formatMoney(inv.amountTtc, { decimals: 2 })}
                      </span>
                      {inv.attachmentId != null ? (
                        <button
                          type="button"
                          onClick={() => openAttachment(inv.attachmentId!)}
                          className="text-xs text-sky-300 hover:underline w-16 text-right"
                          title={t("bankStatements.openInvoicePdfTitle")}
                        >
                          📄 {t("bankStatements.open")}
                        </button>
                      ) : (
                        <span className="text-xs text-amber-400 w-16 text-right">
                          ⚠ {t("bankStatements.noPdf")}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <label
            className={`block rounded-md border border-dashed px-4 py-6 text-center text-sm cursor-pointer transition-colors ${
              busy
                ? "border-neutral-800 text-neutral-600"
                : "border-sky-500/50 text-sky-300 hover:border-sky-400 hover:text-sky-200"
            }`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (!busy) void attachFiles(e.dataTransfer.files);
            }}
          >
            {busy ? (
              (msg ?? t("bankStatements.sendingEllipsis"))
            ) : (
              <>
                <span className="block font-medium">+ {t("bankStatements.attachPdfsFolder")}</span>
                <span className="block text-[11px] text-neutral-500 mt-1">
                  {t("bankStatements.matchByFilenameHint")}
                </span>
              </>
            )}
            <input
              type="file"
              accept="application/pdf"
              multiple
              ref={(el) => {
                if (el) el.setAttribute("webkitdirectory", "");
              }}
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                void attachFiles(e.target.files);
                e.currentTarget.value = "";
              }}
            />
          </label>
          <label className="block text-center text-[11px] text-neutral-500 underline cursor-pointer hover:text-neutral-300">
            {t("bankStatements.chooseIndividualFiles")}
            <input
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                void attachFiles(e.target.files);
                e.currentTarget.value = "";
              }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function StatementExportModal({
  companyId,
  dateFrom,
  dateTo,
  onClose,
}: {
  companyId: number;
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
}) {
  const modals = useModals();
  const t = useT();
  // E-mail do contador salvo POR EMPRESA (ES e BS têm contadores diferentes).
  const accountantEmailKey = `eurostorm.accountantEmail.${companyId}`;
  const [recipient, setRecipient] = useState<string>(
    () => localStorage.getItem(accountantEmailKey) ?? "",
  );
  const [urls, setUrls] = useState<{ pdfUrl: string; csvUrl: string } | null>(null);
  const [lang, setLang] = useState<"fr" | "pt" | "en">("fr");
  // Extrato ORIGINAL do banco anexado pelo operador (PDF/CSV bruto).
  const [originals, setOriginals] = useState<OriginalStatement[]>([]);
  const hasPeriod = Boolean(dateFrom && dateTo);

  const exportMut = trpc.bankTransactions.exportStatement.useMutation({
    onSuccess: (res) => setUrls({ pdfUrl: res.pdfUrl, csvUrl: res.csvUrl }),
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });
  const emailMut = trpc.bankTransactions.emailStatementToAccountant.useMutation({
    onSuccess: (res) => {
      localStorage.setItem(accountantEmailKey, recipient.trim());
      void modals.alert({
        title: t("bankStatements.sentTitle"),
        message: `${t("bankStatements.statementSentTo")} ${res.recipient} (${res.count} ${t("bankStatements.txCountMany")}).`,
        tone: "default",
      });
    },
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: `${t("bankStatements.sendFailed")}: ${e.message}`,
        tone: "danger",
      }),
  });

  function doExport() {
    if (!hasPeriod) return;
    exportMut.mutate({ companyId, dateFrom, dateTo, lang });
  }
  async function handlePickOriginals(files: FileList | null) {
    if (!files || files.length === 0) return;
    try {
      const picked = await Promise.all(
        Array.from(files).map(async (f) => ({
          filename: f.name,
          contentBase64: await fileToBase64(f),
          contentType: f.type || "application/octet-stream",
        })),
      );
      // Acumula (dedup por nome) e limita a 5 (mesmo teto do backend).
      setOriginals((prev) => {
        const byName = new Map(prev.map((p) => [p.filename, p]));
        for (const p of picked) byName.set(p.filename, p);
        return Array.from(byName.values()).slice(0, 5);
      });
    } catch {
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: t("bankStatements.readFileError"),
        tone: "danger",
      });
    }
  }

  async function doEmail() {
    if (!hasPeriod) return;
    const email = recipient.trim();
    if (!email || !email.includes("@")) {
      void modals.alert({
        title: t("bankStatements.invalidEmailTitle"),
        message: t("bankStatements.invalidEmailMessage"),
        tone: "danger",
      });
      return;
    }
    // Lembrete: não anexou o extrato original do banco.
    if (originals.length === 0) {
      const ok = await modals.confirm({
        title: t("bankStatements.attachOriginalTitle"),
        message: t("bankStatements.attachOriginalMessage"),
        confirmLabel: t("bankStatements.sendAnyway"),
        tone: "danger",
      });
      if (!ok) return;
    }
    emailMut.mutate({
      companyId,
      dateFrom,
      dateTo,
      recipientEmail: email,
      lang,
      originalStatements: originals.length > 0 ? originals : undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-lg border border-neutral-800 bg-neutral-950 shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-800 px-5 py-3">
          <h2 className="text-lg font-semibold text-neutral-100">
            {t("bankStatements.exportModalTitle")}
          </h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-300">
            ✕
          </button>
        </div>
        <div className="space-y-4 p-5">
          {!hasPeriod ? (
            <p className="text-sm text-amber-300">{t("bankStatements.selectPeriodFirst")}</p>
          ) : (
            <p className="text-sm text-neutral-400">
              {t("bankStatements.periodLabel")}:{" "}
              <strong className="text-neutral-200">{dateFrom}</strong> {t("bankStatements.toDate")}{" "}
              <strong className="text-neutral-200">{dateTo}</strong>
            </p>
          )}

          <div className="flex items-center gap-2">
            <label className="text-xs text-neutral-400">{t("bankStatements.languageLabel")}:</label>
            <select
              value={lang}
              onChange={(e) => {
                setLang(e.target.value as "fr" | "pt" | "en");
                setUrls(null);
              }}
              className={bankInputClass}
            >
              <option value="fr">{t("bankStatements.langFr")}</option>
              <option value="pt">{t("bankStatements.langPt")}</option>
              <option value="en">{t("bankStatements.langEn")}</option>
            </select>
          </div>

          <div className="rounded-md border border-neutral-800 p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
              {t("bankStatements.downloadSection")}
            </p>
            <button
              onClick={doExport}
              disabled={!hasPeriod || exportMut.isPending}
              className="rounded-md border border-sky-500/50 bg-sky-500/10 px-3 py-1.5 text-sm text-sky-300 hover:bg-sky-500/20 disabled:opacity-40"
            >
              {exportMut.isPending
                ? t("bankStatements.generating")
                : t("bankStatements.generatePdfCsv")}
            </button>
            {urls && (
              <div className="mt-3 flex gap-4 text-sm">
                <a
                  href={urls.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lime underline"
                >
                  📄 {t("bankStatements.downloadPdf")}
                </a>
                <a
                  href={urls.csvUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lime underline"
                >
                  ⬇ {t("bankStatements.downloadCsv")}
                </a>
              </div>
            )}
          </div>

          <div className="rounded-md border border-neutral-800 p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
              {t("bankStatements.emailSection")}
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={t("bankStatements.emailPlaceholder")}
                className={bankInputClass + " flex-1"}
              />
              <button
                onClick={() => void doEmail()}
                disabled={!hasPeriod || emailMut.isPending}
                className="rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-neutral-900 hover:opacity-90 disabled:opacity-40"
              >
                {emailMut.isPending
                  ? t("bankStatements.sendingEllipsis")
                  : `✉ ${t("bankStatements.send")}`}
              </button>
            </div>

            {/* Extrato ORIGINAL do banco — vai junto com o relevé rapproché. */}
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-amber-300">
                ⚠ {t("bankStatements.attachOriginalLabel")}
              </label>
              <input
                type="file"
                accept=".pdf,.csv,application/pdf,text/csv"
                multiple
                onChange={(e) => {
                  void handlePickOriginals(e.target.files);
                  e.target.value = ""; // permite re-selecionar o mesmo arquivo
                }}
                className="block w-full text-xs text-neutral-400 file:mr-3 file:rounded-md file:border file:border-neutral-700 file:bg-neutral-800 file:px-3 file:py-1.5 file:text-neutral-200 hover:file:border-neutral-500"
              />
              {originals.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {originals.map((f) => (
                    <li
                      key={f.filename}
                      className="flex items-center justify-between rounded border border-neutral-800 bg-neutral-900 px-2 py-1 text-[11px] text-neutral-300"
                    >
                      <span className="truncate">📎 {f.filename}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setOriginals((prev) => prev.filter((p) => p.filename !== f.filename))
                        }
                        className="ml-2 shrink-0 text-neutral-500 hover:text-red-400"
                        title={t("bankStatements.removeTitle")}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="mt-2 text-[11px] text-neutral-500">{t("bankStatements.emailNote")}</p>
          </div>
        </div>
        <div className="flex justify-end border-t border-neutral-800 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-4 py-1.5 text-sm text-neutral-300 hover:border-neutral-500"
          >
            {t("bankStatements.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modal de categorização — chips por categoria, sem campo texto livre
// ────────────────────────────────────────────────────────────────────────────

type CategoryValue =
  | "revenue"
  | "supplier_payment"
  | "tax"
  | "salary"
  | "bank_fee"
  | "transfer"
  | "other"
  | "locations_charges_locatives"
  | "primes_assurance"
  | "deplacements_reception"
  | "fournitures_consommables"
  | "intermediaires_honoraires"
  | "vat_payment"
  | "personal"
  | "partner_contribution"
  | "partner_reimbursement";

function buildCategoryOptions(t: ReturnType<typeof useT>): ReadonlyArray<{
  value: CategoryValue;
  label: string;
  hint: string;
  border: string;
  bg: string;
  text: string;
}> {
  return [
    {
      value: "revenue",
      label: t("bankStatements.catRevenue"),
      hint: t("bankStatements.hintRevenue"),
      border: "border-lime/40 hover:border-lime",
      bg: "bg-lime/5 hover:bg-lime/10",
      text: "text-lime",
    },
    {
      value: "supplier_payment",
      label: t("bankStatements.catSupplier"),
      hint: t("bankStatements.hintSupplier"),
      border: "border-amber-500/40 hover:border-amber-500",
      bg: "bg-amber-500/5 hover:bg-amber-500/10",
      text: "text-amber-300",
    },
    {
      value: "tax",
      label: t("bankStatements.catTax"),
      hint: t("bankStatements.hintTax"),
      border: "border-red-500/40 hover:border-red-500",
      bg: "bg-red-500/5 hover:bg-red-500/10",
      text: "text-red-300",
    },
    {
      value: "salary",
      label: t("bankStatements.catSalary"),
      hint: t("bankStatements.hintSalary"),
      border: "border-sky-500/40 hover:border-sky-500",
      bg: "bg-sky-500/5 hover:bg-sky-500/10",
      text: "text-sky-300",
    },
    {
      value: "bank_fee",
      label: t("bankStatements.catBankFee"),
      hint: t("bankStatements.hintBankFee"),
      border: "border-purple-500/40 hover:border-purple-500",
      bg: "bg-purple-500/5 hover:bg-purple-500/10",
      text: "text-purple-300",
    },
    {
      value: "transfer",
      label: t("bankStatements.catTransfer"),
      hint: t("bankStatements.hintTransfer"),
      border: "border-cyan-500/40 hover:border-cyan-500",
      bg: "bg-cyan-500/5 hover:bg-cyan-500/10",
      text: "text-cyan-300",
    },
    {
      value: "other",
      label: t("bankStatements.catOther"),
      hint: t("bankStatements.hintOther"),
      border: "border-neutral-700 hover:border-neutral-500",
      bg: "bg-neutral-800/40 hover:bg-neutral-800/60",
      text: "text-neutral-300",
    },
    // F-monthly-close — custos alinhados à taxonomia fiscal (alimentam o SIG/lucro).
    {
      value: "locations_charges_locatives",
      label: t("bankStatements.catRent"),
      hint: t("bankStatements.hintRent"),
      border: "border-teal-500/40 hover:border-teal-500",
      bg: "bg-teal-500/5 hover:bg-teal-500/10",
      text: "text-teal-300",
    },
    {
      value: "primes_assurance",
      label: t("bankStatements.catInsurance"),
      hint: t("bankStatements.hintInsurance"),
      border: "border-indigo-500/40 hover:border-indigo-500",
      bg: "bg-indigo-500/5 hover:bg-indigo-500/10",
      text: "text-indigo-300",
    },
    {
      value: "deplacements_reception",
      label: t("bankStatements.catTravel"),
      hint: t("bankStatements.hintTravel"),
      border: "border-blue-500/40 hover:border-blue-500",
      bg: "bg-blue-500/5 hover:bg-blue-500/10",
      text: "text-blue-300",
    },
    {
      value: "fournitures_consommables",
      label: t("bankStatements.catSupplies"),
      hint: t("bankStatements.hintSupplies"),
      border: "border-violet-500/40 hover:border-violet-500",
      bg: "bg-violet-500/5 hover:bg-violet-500/10",
      text: "text-violet-300",
    },
    {
      value: "intermediaires_honoraires",
      label: t("bankStatements.catFees"),
      hint: t("bankStatements.hintFees"),
      border: "border-fuchsia-500/40 hover:border-fuchsia-500",
      bg: "bg-fuchsia-500/5 hover:bg-fuchsia-500/10",
      text: "text-fuchsia-300",
    },
    {
      value: "vat_payment",
      label: t("bankStatements.catVatPayment"),
      hint: t("bankStatements.hintVatPayment"),
      border: "border-yellow-500/40 hover:border-yellow-500",
      bg: "bg-yellow-500/5 hover:bg-yellow-500/10",
      text: "text-yellow-300",
    },
    // F-partner-account — fluxos do sócio (compte courant). Entram no "saldo do sócio".
    {
      value: "personal",
      label: t("bankStatements.catPersonal"),
      hint: t("bankStatements.hintPersonal"),
      border: "border-rose-500/40 hover:border-rose-500",
      bg: "bg-rose-500/5 hover:bg-rose-500/10",
      text: "text-rose-300",
    },
    {
      value: "partner_contribution",
      label: t("bankStatements.catPartnerContribution"),
      hint: t("bankStatements.hintPartnerContribution"),
      border: "border-emerald-500/40 hover:border-emerald-500",
      bg: "bg-emerald-500/5 hover:bg-emerald-500/10",
      text: "text-emerald-300",
    },
    {
      value: "partner_reimbursement",
      label: t("bankStatements.catPartnerReimbursement"),
      hint: t("bankStatements.hintPartnerReimbursement"),
      border: "border-orange-500/40 hover:border-orange-500",
      bg: "bg-orange-500/5 hover:bg-orange-500/10",
      text: "text-orange-300",
    },
  ];
}

function CategorizeModal({
  companyId,
  transactionId,
  onClose,
  onSaved,
}: {
  companyId: number;
  transactionId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const modals = useModals();
  const t = useT();
  const CATEGORY_LABELS = buildCategoryLabels(t);
  const CATEGORY_OPTIONS = buildCategoryOptions(t);
  const txQuery = trpc.bankTransactions.getById.useQuery({
    companyId,
    transactionId,
  });

  const updateMutation = trpc.bankTransactions.updateCategory.useMutation({
    onSuccess: onSaved,
    onError: (e) =>
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  const tx = txQuery.data;
  const currentCategory = tx?.category ?? null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("bankStatements.categorizeTitle")}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-4">
          {tx && (
            <div className="bg-neutral-800/30 rounded-md px-3 py-2 text-sm">
              <div className="text-xs uppercase tracking-wider text-neutral-500">
                {t("bankStatements.bankTransaction")}
              </div>
              <div className="font-mono text-neutral-200">
                {tx.transactionDate} ·{" "}
                <span className={Number(tx.amount) > 0 ? "text-lime" : "text-red-400"}>
                  {formatMoney(tx.amount, { decimals: 2 })}
                </span>
              </div>
              <div className="text-neutral-300 truncate" title={tx.description ?? ""}>
                {tx.description ?? "—"}
              </div>
              {currentCategory && (
                <div className="mt-1 text-xs text-neutral-500">
                  {t("bankStatements.currentCategoryLabel")}:{" "}
                  <span className="text-neutral-300">
                    {CATEGORY_LABELS[currentCategory] ?? currentCategory}
                  </span>{" "}
                  ·{" "}
                  <span className={CONFIDENCE_TONE[tx.categoryConfidence] ?? "text-neutral-500"}>
                    {tx.categoryConfidence}
                  </span>
                </div>
              )}
            </div>
          )}

          <p className="text-sm text-neutral-400">
            {t("bankStatements.categorizeHelpPre")}{" "}
            <span className="text-amber-300">{t("bankStatements.manualWord")}</span>{" "}
            {t("bankStatements.categorizeHelpSuf")}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CATEGORY_OPTIONS.map((opt) => {
              const isCurrent = currentCategory === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() =>
                    updateMutation.mutate({
                      companyId,
                      transactionId,
                      category: opt.value,
                    })
                  }
                  disabled={updateMutation.isPending}
                  className={`rounded-md border ${opt.border} ${opt.bg} ${opt.text} px-3 py-2 text-sm font-medium text-left flex items-center justify-between gap-2 disabled:opacity-50 transition-colors`}
                >
                  <span>
                    <div className="font-semibold">
                      {opt.label}
                      {isCurrent && (
                        <span className="ml-2 text-xs">✓ {t("bankStatements.current")}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-neutral-400 mt-0.5">{opt.hint}</div>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-neutral-800">
            <button
              type="button"
              onClick={() =>
                updateMutation.mutate({
                  companyId,
                  transactionId,
                  category: null,
                })
              }
              disabled={updateMutation.isPending || currentCategory === null}
              className="rounded-md border border-neutral-800 hover:border-red-500/40 hover:text-red-300 disabled:opacity-30 text-neutral-400 px-3 py-1.5 text-xs"
            >
              {t("bankStatements.clearCategory")}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={updateMutation.isPending}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
            >
              {t("bankStatements.cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modal de reconciliação manual — busca alvo entre receipts/payments pendentes
// ────────────────────────────────────────────────────────────────────────────
type LinkKind =
  | "customer_receipt"
  | "technician_payment"
  | "tax_payment"
  | "purchase_invoice"
  | "proforma_request";
function buildLinkChips(
  t: ReturnType<typeof useT>,
): ReadonlyArray<{ value: LinkKind; label: string }> {
  return [
    { value: "customer_receipt", label: t("bankStatements.linkCustomerReceipt") },
    { value: "technician_payment", label: t("bankStatements.linkTechnicianPayment") },
    { value: "tax_payment", label: t("bankStatements.linkTaxPayment") },
    { value: "purchase_invoice", label: t("bankStatements.linkPurchaseInvoice") },
    { value: "proforma_request", label: t("bankStatements.linkProforma") },
  ];
}

// F-bank-register-purchase-invoice — categorias de fatura de fornecedor (espelha
// o categoryEnum do router purchaseInvoices).
const PI_CATEGORIES = [
  "achats_consommes",
  "fournitures_consommables",
  "locations_charges_locatives",
  "primes_assurance",
  "intermediaires_honoraires",
  "deplacements_reception",
  "frais_bancaires",
  "impots_taxes",
  "charges_personnel",
  "autres_charges_exploitation",
  "autres",
] as const;
type PiCategory = (typeof PI_CATEGORIES)[number];
function buildPiCategoryLabels(t: ReturnType<typeof useT>): Record<PiCategory, string> {
  return {
    achats_consommes: t("bankStatements.piAchatsConsommes"),
    fournitures_consommables: t("bankStatements.piFournitures"),
    locations_charges_locatives: t("bankStatements.piLocations"),
    primes_assurance: t("bankStatements.piAssurances"),
    intermediaires_honoraires: t("bankStatements.piHonoraires"),
    deplacements_reception: t("bankStatements.piDeplacements"),
    frais_bancaires: t("bankStatements.piFraisBancaires"),
    impots_taxes: t("bankStatements.piImpots"),
    charges_personnel: t("bankStatements.piChargesPersonnel"),
    autres_charges_exploitation: t("bankStatements.piAutresCharges"),
    autres: t("bankStatements.catOther"),
  };
}

/**
 * F-bank-register-purchase-invoice — registra uma fatura de fornecedor inline
 * (quando não existe pendente pra vincular), anexa o PDF (opcional) e concilia o
 * pagamento a ela de uma vez. Valor HT pré-preenchido = |depósito| ÷ 1,20.
 */
function NewPurchaseInvoiceForm({
  companyId,
  transactionId,
  depositAmount,
  onSaved,
}: {
  companyId: number;
  transactionId: number;
  depositAmount: number;
  onSaved: () => void;
}) {
  const modals = useModals();
  const t = useT();
  const PI_CATEGORY_LABELS = buildPiCategoryLabels(t);
  const suppliersQuery = trpc.suppliers.list.useQuery({ companyId, limit: 200, offset: 0 });
  const suppliers = suppliersQuery.data?.rows ?? [];
  const [supplierId, setSupplierId] = useState<number | "">("");
  const [number, setNumber] = useState("");
  const [category, setCategory] = useState<PiCategory>("autres");
  const [ht, setHt] = useState(() => (depositAmount > 0 ? (depositAmount / 1.2).toFixed(2) : ""));
  const [tva, setTva] = useState("20");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const createMut = trpc.purchaseInvoices.create.useMutation();
  const reconcileMut = trpc.bankTransactions.reconcile.useMutation();

  async function submit() {
    const htNum = parseFloat(ht.replace(",", "."));
    const tvaNum = parseFloat(tva.replace(",", "."));
    if (supplierId === "" || !number.trim()) {
      void modals.alert({
        title: t("bankStatements.missingDataTitle"),
        message: t("bankStatements.missingDataMessage"),
        tone: "danger",
      });
      return;
    }
    if (
      !Number.isFinite(htNum) ||
      htNum < 0 ||
      !Number.isFinite(tvaNum) ||
      tvaNum < 0 ||
      tvaNum > 30
    ) {
      void modals.alert({
        title: t("bankStatements.invalidAmountTitle"),
        message: t("bankStatements.invalidAmountMessage"),
        tone: "danger",
      });
      return;
    }
    setBusy(true);
    try {
      const created = await createMut.mutateAsync({
        companyId,
        data: {
          supplierId,
          invoiceNumberExternal: number.trim(),
          category,
          totalHt: htNum,
          tvaRate: tvaNum,
        },
      });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("companyId", String(companyId));
        fd.append("ownerId", String(created.id));
        fd.append("ownerKind", "purchase_invoice");
        const resp = await fetch("/uploads", { method: "POST", credentials: "include", body: fd });
        if (!resp.ok) throw new Error(t("bankStatements.invoiceCreatedPdfFailed"));
      }
      await reconcileMut.mutateAsync({
        companyId,
        transactionId,
        kind: "purchase_invoice",
        targetId: created.id,
      });
      onSaved();
    } catch (e) {
      void modals.alert({
        title: t("bankStatements.errorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.03] px-4 py-3 space-y-3">
      <p className="text-xs text-neutral-400">{t("bankStatements.newPurchaseHelp")}</p>
      <div className="grid grid-cols-2 gap-2">
        <select
          value={supplierId === "" ? "" : String(supplierId)}
          onChange={(e) => setSupplierId(e.target.value === "" ? "" : Number(e.target.value))}
          className={bankInputClass + " w-full"}
        >
          <option value="">{t("bankStatements.supplierPlaceholderOption")}</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.displayName}
            </option>
          ))}
        </select>
        <input
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder={t("bankStatements.invoiceNumberPlaceholder")}
          className={bankInputClass + " w-full"}
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as PiCategory)}
          className={bankInputClass + " w-full"}
        >
          {PI_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {PI_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <input
            value={ht}
            onChange={(e) => setHt(e.target.value)}
            placeholder={t("bankStatements.amountHtPlaceholder")}
            className={bankInputClass + " w-full font-mono"}
          />
          <input
            value={tva}
            onChange={(e) => setTva(e.target.value)}
            placeholder="TVA%"
            className={bankInputClass + " w-16 font-mono"}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <label className="cursor-pointer rounded-md border border-sky-500/50 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-300 hover:bg-sky-500/20">
          📎 {file ? t("bankStatements.changePdf") : t("bankStatements.attachInvoicePdf")}
          <input
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {file ? (
          <span className="flex items-center gap-1 text-xs text-neutral-300">
            <span className="max-w-[170px] truncate" title={file.name}>
              {file.name}
            </span>
            <button
              type="button"
              onClick={() => setFile(null)}
              className="text-neutral-500 hover:text-red-300"
              title={t("bankStatements.removeFileTitle")}
            >
              ✕
            </button>
          </span>
        ) : (
          <span className="text-xs text-neutral-500">{t("bankStatements.pdfOptional")}</span>
        )}
        <button
          onClick={submit}
          disabled={busy}
          className="ml-auto rounded-md bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white px-4 py-1.5 text-sm font-medium"
        >
          {busy ? t("bankStatements.registering") : t("bankStatements.registerAndReconcile")}
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// F-conciliacao-passos (auditoria UX 23/10/2026, item 6 — B1/B2; CEO 24/10
// "pode seguir") — a janela "3 modos + 27 chips + tabela de alvos" virou um
// PAINEL LATERAL em 3 passos, com a lista visível atrás:
//   1 "O que é?"  → 3 opções grandes (recebimento de cliente · pagamento de
//                   um documento · sem documento), com a sugestão do Gestion
//                   marcada (fatura/proforma com o nº no virement ou o mesmo
//                   valor; ou o histórico de transações parecidas);
//   2 "Detalhes"  → só os campos daquele tipo;
//   3 "Confirmar" → resumo em linguagem corrente ("Vai ficar: …") + Confirmar.
// As mutations são as MESMAS da janela antiga (reconcileToInvoice, reconcile,
// reconcileMulti, reconcileDirect); no servidor só entrou a consulta
// bankTransactions.similarHistory (B2).
// ────────────────────────────────────────────────────────────────────────────
type SheetType = "receipt" | "document" | "direct";
type DocKind = Exclude<LinkKind, "customer_receipt">;
type Passo = 1 | 2 | 3;

interface Alvo {
  key: string;
  kind: "invoice" | "receipt" | "doc";
  id: number;
  label: string;
  amount: string | null;
  date: string | null;
  hint: string | null;
  detalhe: string | null;
  rank: number;
}

const ehMotivo = (r: string | null | undefined): r is BankDirectReasonKey =>
  !!r && (BANK_DIRECT_REASON_KEYS as readonly string[]).includes(r);

function ReconcileSheet({
  companyId,
  transactionId,
  onClose,
  onSaved,
}: {
  companyId: number;
  transactionId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  // `tr` (e não `t`) porque `t` é o nome do item nos maps de alvos.
  const tr = useT();
  const { locale } = useI18n();
  // F-avoir-na-conciliacao (CEO 2026-10-25): criar o avoir é ação de
  // Administrador (mesma regra do "↩ Emitir avoir" em Faturas).
  const { user: usuario } = useAuth();
  const isAdmin =
    usuario?.isSuperAdmin === true ||
    (usuario?.memberships ?? []).some(
      (m) => m.companyId === companyId && m.active && m.role === "admin",
    );
  const CATEGORIAS = buildCategoryOptions(tr);
  // Ordem do dia a dia: proforma (técnicos/grupo) > fornecedor > técnico > imposto.
  const ORDEM_DOC: readonly DocKind[] = [
    "proforma_request",
    "purchase_invoice",
    "technician_payment",
    "tax_payment",
  ];
  const DOC_KINDS = buildLinkChips(tr)
    .filter((c): c is { value: DocKind; label: string } => c.value !== "customer_receipt")
    .sort((a, b) => ORDEM_DOC.indexOf(a.value) - ORDEM_DOC.indexOf(b.value));

  const [step, setStep] = useState<Passo>(1);
  const [tipo, setTipo] = useState<SheetType | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // Recebimento de cliente (CEO 2026-10-25: "o pagamento se refere a várias
  // faturas"): a lista marca UMA ou VÁRIAS faturas/recebimentos com caixas de
  // seleção; a soma e a diferença para o depósito aparecem enquanto marca.
  // 1 fatura → fluxo simples (parcial/integral no passo 3); 2+ → modo várias.
  const [selInvoices, setSelInvoices] = useState<Set<number>>(new Set());
  const [selReceipts, setSelReceipts] = useState<Set<number>>(new Set());
  const [filtroCliente, setFiltroCliente] = useState<string>("all");
  const [filtroNumero, setFiltroNumero] = useState("");
  // Depósito menor que a fatura: parcial (padrão), quitar mesmo assim, ou a
  // diferença é um AVOIR (cria a nota de crédito e dá a fatura por paga).
  const [parcial, setParcial] = useState<"partial" | "full" | "avoir">("partial");
  const [avoirTipo, setAvoirTipo] = useState<"commercial" | "return" | "error">("commercial");
  const [avoirMotivo, setAvoirMotivo] = useState("");
  // F-pay-allocation — parcial em fatura multi-carro: escolher os carros quitados.
  const [allocFor, setAllocFor] = useState<{ invoiceId: number; ht: number } | null>(null);
  // Pagamento de um documento.
  const [docKind, setDocKind] = useState<DocKind>("proforma_request");
  const [selDoc, setSelDoc] = useState<number | null>(null);
  const [novaFatura, setNovaFatura] = useState(false);
  // Sem documento (categoria + motivo padronizado + complemento).
  const [directCategory, setDirectCategory] = useState<CategoryValue>("supplier_payment");
  const [directReason, setDirectReason] = useState<BankDirectReasonKey | "">("");
  const [directDescription, setDirectDescription] = useState("");
  const [sugAplicada, setSugAplicada] = useState(false);
  // F-adiantamento-tecnico (CEO 2026-10-25) — saída sem documento que é
  // ADIANTAMENTO a técnico (hotel, ferramentas…): rateio por técnico; cada
  // parte vira um adiantamento aberto, descontado na próxima fatura dele.
  const [ehAdiant, setEhAdiant] = useState(false);
  const [advRows, setAdvRows] = useState<
    Array<{ technicianId: number | ""; amount: string; description: string }>
  >([{ technicianId: "", amount: "", description: "" }]);
  const techsQuery = trpc.technicians.list.useQuery(
    { companyId, active: true, limit: 200, offset: 0 },
    { enabled: ehAdiant },
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const txQuery = trpc.bankTransactions.getById.useQuery({ companyId, transactionId });
  const tx = txQuery.data;
  const valor = Number(tx?.amount ?? 0);
  const credito = valor > 0;
  const depositoAbs = Math.abs(valor);
  const descTx = `${tx?.description ?? ""} ${tx?.rawDescription ?? ""}`
    .toUpperCase()
    .replace(/\s+/g, "");
  // Referências de fatura citadas no virement, inclusive encadeadas
  // ("VIRT FAC 2026-00481/00460/00461" → 2026-481, 2026-460, 2026-461).
  const refsNoVirement = (() => {
    const out = new Set<string>();
    const texto = `${tx?.description ?? ""} ${tx?.rawDescription ?? ""}`;
    for (const m of texto.matchAll(/(\d{4})\s*-\s*(\d{3,6})((?:\s*\/\s*\d{3,6})*)/g)) {
      const ano = m[1]!;
      out.add(`${ano}-${Number(m[2])}`);
      for (const n of (m[3] ?? "").split("/")) {
        const seq = n.trim();
        if (seq) out.add(`${ano}-${Number(seq)}`);
      }
    }
    return out;
  })();
  const chaveFatura = (numero: string): string | null => {
    const m = numero.match(/(\d{4})\s*-?\s*(\d{3,6})/);
    return m ? `${m[1]}-${Number(m[2])}` : null;
  };

  // Consultas. As que alimentam a SUGESTÃO do passo 1 carregam logo (fatura
  // aberta p/ crédito; proforma e fatura de fornecedor p/ débito; histórico);
  // as outras só quando o tipo é escolhido.
  const historyQuery = trpc.bankTransactions.similarHistory.useQuery({ companyId, transactionId });
  const openInvQuery = trpc.customerInvoices.list.useQuery(
    { companyId, openForBank: true, limit: 200, offset: 0 },
    { enabled: !!tx && (credito || tipo === "receipt") },
  );
  const receiptsQuery = trpc.customerReceipts.list.useQuery(
    { companyId, reconciled: false, linkable: true, limit: 200, offset: 0 },
    { enabled: tipo === "receipt" },
  );
  const proformasQuery = trpc.proformaRequests.list.useQuery(
    { companyId, limit: 200, offset: 0 },
    { enabled: !!tx && (!credito || tipo === "document") },
  );
  const purchaseInvoicesQuery = trpc.purchaseInvoices.list.useQuery(
    { companyId, status: "received", limit: 50, offset: 0 },
    { enabled: !!tx && (!credito || tipo === "document") },
  );
  const techPaymentsQuery = trpc.technicianPayments.list.useQuery(
    { companyId, reconciled: false, limit: 50, offset: 0 },
    { enabled: tipo === "document" && docKind === "technician_payment" },
  );
  const taxPaymentsQuery = trpc.taxPayments.list.useQuery(
    { companyId, limit: 50, offset: 0 },
    { enabled: tipo === "document" && docKind === "tax_payment" },
  );

  const aoErrar = (e: { message: string }) => setErro(e.message);
  const reconcileInvoiceMutation = trpc.reconciliation.reconcileToInvoice.useMutation({
    onSuccess: onSaved,
    onError: aoErrar,
  });
  const reconcileMutation = trpc.bankTransactions.reconcile.useMutation({
    onSuccess: onSaved,
    onError: aoErrar,
  });
  const reconcileDirectMutation = trpc.bankTransactions.reconcileDirect.useMutation({
    onSuccess: onSaved,
    onError: aoErrar,
  });
  const reconcileMultiMutation = trpc.reconciliation.reconcileMulti.useMutation({
    onSuccess: onSaved,
    onError: aoErrar,
  });
  const avoirMutation = trpc.customerCreditNotes.create.useMutation();
  const pending =
    reconcileInvoiceMutation.isPending ||
    reconcileMutation.isPending ||
    reconcileDirectMutation.isPending ||
    reconcileMultiMutation.isPending ||
    avoirMutation.isPending;

  const money = (v: number | string | null | undefined) => formatMoney(v ?? 0, { decimals: 2 });

  // FATURADO TTC = o que o cliente paga (base do depósito), NÃO o devis nem o
  // HT; respeita autoliquidação. Fatura aberta vale o que o cliente AINDA deve:
  // TTC − avoirs emitidos − já recebido (recibo é HT → TTC pela TVA da fatura).
  const faturadoTtc = (o: {
    totalTtc: string | null;
    totalFaturadoSnapshot: string | null;
    tvaRate: string | null;
    tvaReverseCharge: boolean | null;
  }): number => {
    if (o.totalTtc != null) return Number(o.totalTtc);
    const ht = Number(o.totalFaturadoSnapshot ?? 0);
    if (o.tvaReverseCharge) return ht;
    return ht * (1 + Number(o.tvaRate ?? 20) / 100);
  };
  const recebidoTtc = (o: {
    receivedHt?: number | null;
    tvaRate: string | null;
    tvaReverseCharge: boolean | null;
  }): number => {
    const ht = Number(o.receivedHt ?? 0);
    if (ht === 0) return 0;
    const rate = o.tvaReverseCharge ? 0 : Number(o.tvaRate ?? 20);
    return ht * (1 + rate / 100);
  };
  const saldoTtc = (
    o: Parameters<typeof faturadoTtc>[0] & {
      creditNotesTtc?: number | null;
      receivedHt?: number | null;
    },
  ): number => Math.max(0, faturadoTtc(o) - Number(o.creditNotesTtc ?? 0) - recebidoTtc(o));
  const contaAvoir = (o: {
    totalTtc: string | null;
    totalFaturadoSnapshot: string | null;
    tvaRate: string | null;
    tvaReverseCharge: boolean | null;
    creditNotesTtc?: number | null;
    creditNotesEmbeddedTtc?: number | null;
    creditNoteNumbers?: string[] | null;
  }): string | null => {
    const nums = o.creditNoteNumbers ?? [];
    if (nums.length === 0) return null;
    const emb = Number(o.creditNotesEmbeddedTtc ?? 0);
    const doc = faturadoTtc(o) + emb;
    const av = Number(o.creditNotesTtc ?? 0) + emb;
    return `${tr("bankStatements.invoiceDocWord")} ${money(doc)} − ${tr("bankStatements.avoirWord")} ${nums.join(", ")} ${money(av)}`;
  };
  const rTtc = (r: {
    invoiceTotalTtc: string | null;
    invoiceFaturado: string | null;
    invoiceTvaRate: string | null;
    invoiceReverseCharge: boolean | null;
    amount: string;
  }): number =>
    r.invoiceFaturado != null
      ? faturadoTtc({
          totalTtc: r.invoiceTotalTtc,
          totalFaturadoSnapshot: r.invoiceFaturado,
          tvaRate: r.invoiceTvaRate,
          tvaReverseCharge: r.invoiceReverseCharge,
        })
      : Number(r.amount ?? 0);

  // Faturas abertas: primeiro a que tem o nº no virement, depois valor exato
  // (TTC ou HT), depois nº da fatura (maior → menor).
  const faturasAbertas = (openInvQuery.data?.rows ?? [])
    .map((i) => {
      const ttc = Math.round(saldoTtc(i) * 100) / 100;
      const ht =
        Number(i.totalFaturadoSnapshot ?? 0) -
        Number(i.creditNotesHt ?? 0) -
        Number(i.receivedHt ?? 0);
      const num = i.invoiceNumber.toUpperCase().replace(/\s+/g, "");
      const chave = chaveFatura(i.invoiceNumber);
      const porNumero =
        (num.length >= 4 && descTx.includes(num)) || (chave != null && refsNoVirement.has(chave));
      const porValor = Math.abs(ttc - depositoAbs) < 0.01 || Math.abs(ht - depositoAbs) < 0.01;
      return {
        id: i.id,
        numero: i.invoiceNumber,
        cliente: i.customerName ?? null,
        customerId: i.customerId ?? null,
        ttc,
        reverseCharge: !!i.tvaReverseCharge,
        tvaRate: Number(i.tvaRate ?? 20),
        multiCarro: i.isConsolidated && i.dossierCount > 1,
        date: i.issuedAt ? new Date(i.issuedAt).toISOString().slice(0, 10) : null,
        hint: porNumero
          ? tr("bankStatements.matchByNumber")
          : porValor
            ? tr("bankStatements.matchByAmount")
            : null,
        rank: porNumero ? 0 : porValor ? 1 : 2,
        detalhe:
          [
            contaAvoir(i),
            Number(i.receivedHt ?? 0) > 0
              ? `${tr("bankStatements.alreadyReceivedPre")} ${money(recebidoTtc(i))} — ${tr("bankStatements.remainingWord")} ${money(ttc)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ") || null,
      };
    })
    .sort(
      (a, b) => a.rank - b.rank || b.numero.localeCompare(a.numero, undefined, { numeric: true }),
    );
  const alvosRecebimento: Alvo[] = [
    ...faturasAbertas.map((f) => ({
      key: `invoice-${f.id}`,
      kind: "invoice" as const,
      id: f.id,
      label: `${tr("bankStatements.openInvoiceWord")} ${f.numero}${f.cliente ? ` · ${f.cliente}` : ""}`,
      amount: f.ttc.toFixed(2),
      date: f.date,
      hint: f.hint,
      detalhe: f.detalhe,
      rank: f.rank,
    })),
    ...(receiptsQuery.data?.rows ?? []).map((r) => ({
      key: `receipt-${r.id}`,
      kind: "receipt" as const,
      id: r.id,
      label: `${tr("bankStatements.receiptWord")} #${r.id} · ${tr("bankStatements.invoiceWord")} ${r.invoiceNumber ?? `#${r.customerInvoiceId}`}${r.invoiceCustomerName ? ` · ${r.invoiceCustomerName}` : ""}`,
      amount: r.amount,
      date: r.receivedAt ? new Date(r.receivedAt).toISOString().slice(0, 10) : null,
      hint: null,
      detalhe: null,
      rank: 3,
    })),
  ];

  // F-bank-proforma — valor igual à saída, nome do destinatário ou nº da
  // fatura registrada no virement sobem para o topo; paga só entra se bater.
  const proformas: Alvo[] = (proformasQuery.data?.rows ?? [])
    .filter(
      (p) =>
        p.status === "invoice_received" ||
        p.status === "approved" ||
        p.status === "sent" ||
        p.status === "paid",
    )
    .map((p) => {
      // F-adiantamento-tecnico — o que a empresa paga é líquido − adiantamentos
      // compensados (a fatura do técnico veio com o desconto).
      const net = Number(p.netTotal ?? 0) - Number(p.advancesDeducted ?? 0);
      const porValor = Math.abs(net - depositoAbs) < 0.01;
      const nome = (p.recipientName ?? "").toUpperCase().replace(/\s+/g, "");
      const porNome = nome.length >= 4 && descTx.includes(nome.slice(0, 12));
      const fatura = p.returnedInvoiceNumber?.trim() || null;
      const numFat = (fatura ?? "").toUpperCase().replace(/\s+/g, "");
      const porFatura = numFat.length >= 4 && descTx.includes(numFat);
      return {
        key: `doc-${p.id}`,
        kind: "doc" as const,
        id: p.id,
        label: `Proforma ${p.reference}${fatura ? ` · ${tr("bankStatements.invoiceWord")} ${fatura}` : ""}${p.recipientName ? ` · ${p.recipientName}` : ""}`,
        amount: net.toFixed(2),
        date:
          (p.sentAt ?? p.createdAt)
            ? new Date(p.sentAt ?? p.createdAt).toISOString().slice(0, 10)
            : null,
        hint: porFatura
          ? tr("bankStatements.matchByNumber")
          : porValor
            ? tr("bankStatements.matchByAmount")
            : null,
        detalhe:
          p.status === "paid"
            ? tr("bankStatements.proformaAlreadyPaid")
            : p.status === "invoice_received"
              ? tr("bankStatements.proformaWillBePaid")
              : p.status === "sent"
                ? tr("bankStatements.proformaNeedsApproval")
                : tr("bankStatements.proformaNeedsInvoice"),
        rank: porFatura ? -1 : (porValor ? 0 : 2) + (porNome ? 0 : 1),
        pagaSemMatch: p.status === "paid" && !porValor && !porNome && !porFatura,
      };
    })
    .filter((x) => !x.pagaSemMatch)
    .sort((a, b) => a.rank - b.rank);
  const compras: Alvo[] = (purchaseInvoicesQuery.data?.rows ?? [])
    .map((p) => {
      const porValor = Math.abs(Number(p.totalTtc) - depositoAbs) < 0.01;
      return {
        key: `doc-${p.id}`,
        kind: "doc" as const,
        id: p.id,
        label: `${tr("bankStatements.purchaseWord")} #${p.id} · ${p.invoiceNumberExternal}`,
        amount: p.totalTtc,
        date: p.issueDate,
        hint: porValor ? tr("bankStatements.matchByAmount") : null,
        detalhe: null,
        rank: porValor ? 0 : 2,
      };
    })
    .sort((a, b) => a.rank - b.rank);
  const pagamentosTec: Alvo[] = (techPaymentsQuery.data?.rows ?? []).map((p) => ({
    key: `doc-${p.id}`,
    kind: "doc" as const,
    id: p.id,
    label: `${tr("bankStatements.linkTechnicianPayment")} #${p.id} · ${tr("bankStatements.invoiceWord")} ${p.invoiceNumber ?? `#${p.technicianInvoiceId}`}`,
    amount: p.amount,
    date: p.paidAt ? new Date(p.paidAt).toISOString().slice(0, 10) : null,
    hint:
      Math.abs(Number(p.amount) - depositoAbs) < 0.01 ? tr("bankStatements.matchByAmount") : null,
    detalhe: null,
    rank: 2,
  }));
  const impostos: Alvo[] = (taxPaymentsQuery.data?.rows ?? []).map((p) => ({
    key: `doc-${p.id}`,
    kind: "doc" as const,
    id: p.id,
    label: `${tr("bankStatements.linkTaxPayment")} #${p.id}`,
    amount: p.amountPaid ?? p.amountDue,
    date: p.paidAt ? new Date(p.paidAt).toISOString().slice(0, 10) : null,
    hint: null,
    detalhe: null,
    rank: 2,
  }));
  const alvosDocumento: Alvo[] =
    docKind === "proforma_request"
      ? proformas
      : docKind === "purchase_invoice"
        ? compras
        : docKind === "technician_payment"
          ? pagamentosTec
          : impostos;

  // ── Sugestão do Gestion (passo 1) ──────────────────────────────────────
  const hist = historyQuery.data ?? null;
  const rotuloHist = hist
    ? [
        hist.category
          ? (CATEGORIAS.find((c) => c.value === hist.category)?.label ?? hist.category)
          : null,
        hist.reason ? bankDirectReasonLabel(hist.reason, locale) : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
  // Só vale como sugestão quando a mesma escolha se repetiu (>= 2); "1 de 2"
  // seria cara ou coroa. O texto mostra "3/5" quando as parecidas divergem.
  const histForte = hist != null && hist.count >= 2;
  const textoHist = histForte
    ? `${hist.count}${hist.total > hist.count ? `/${hist.total}` : ""} ${hist.total === 1 ? tr("bankStatements.sugSimilarOne") : tr("bankStatements.sugSimilarMany")} («${hist.token}») ${hist.total === 1 ? tr("bankStatements.sugSimilarOneSuf") : tr("bankStatements.sugSimilarManySuf")} ${rotuloHist || tr("bankStatements.sumNoReason")}`
    : null;
  interface Sugestao {
    tipo: SheetType;
    docKind?: DocKind;
    alvo?: { kind: "invoice" | "receipt" | "doc"; id: number };
    /** Recebimento: todas as faturas citadas no virement (pré-marcadas). */
    alvos?: number[];
    texto: string;
    direct?: { category: CategoryValue; reason: BankDirectReasonKey | "" };
  }
  const sugestao: Sugestao | null = (() => {
    if (!tx) return null;
    if (credito) {
      const citadas = faturasAbertas.filter((f) => f.rank === 0);
      if (citadas.length > 1) {
        return {
          tipo: "receipt",
          alvos: citadas.map((f) => f.id),
          texto: `${citadas.length} ${tr("bankStatements.sugInvoicesCited")}: ${citadas.map((f) => f.numero).join(", ")}`,
        };
      }
      const f = faturasAbertas[0];
      if (f && f.rank <= 1) {
        return {
          tipo: "receipt",
          alvo: { kind: "invoice", id: f.id },
          texto: `${tr("bankStatements.openInvoiceWord")} ${f.numero}${f.cliente ? ` · ${f.cliente}` : ""} · ${f.hint ?? ""}`,
        };
      }
    } else {
      const p = proformas[0];
      if (p && p.rank <= 0) {
        return {
          tipo: "document",
          docKind: "proforma_request",
          alvo: { kind: "doc", id: p.id },
          texto: `${p.label} · ${p.hint ?? ""}`,
        };
      }
      const c = compras.find((x) => x.hint);
      if (c) {
        return {
          tipo: "document",
          docKind: "purchase_invoice",
          alvo: { kind: "doc", id: c.id },
          texto: `${c.label} · ${c.hint ?? ""}`,
        };
      }
    }
    if (hist && textoHist) {
      if (!hist.kind || hist.kind === "other") {
        const cat = CATEGORIAS.find((c) => c.value === hist.category);
        if (!cat) return null;
        return {
          tipo: "direct",
          texto: textoHist,
          direct: { category: cat.value, reason: ehMotivo(hist.reason) ? hist.reason : "" },
        };
      }
      if (hist.kind === "customer_receipt") return { tipo: "receipt", texto: textoHist };
      const dk = DOC_KINDS.find((k) => k.value === hist.kind);
      if (dk) return { tipo: "document", docKind: dk.value, texto: textoHist };
    }
    return null;
  })();

  function escolherTipo(t: SheetType) {
    setTipo(t);
    setErro(null);
    if (sugestao && sugestao.tipo === t) {
      if (sugestao.docKind) setDocKind(sugestao.docKind);
      if (sugestao.alvos) setSelInvoices(new Set(sugestao.alvos));
      if (sugestao.alvo) {
        if (sugestao.alvo.kind === "doc") setSelDoc(sugestao.alvo.id);
        else if (sugestao.alvo.kind === "invoice") setSelInvoices(new Set([sugestao.alvo.id]));
        else setSelReceipts(new Set([sugestao.alvo.id]));
      }
      if (sugestao.direct) {
        setDirectCategory(sugestao.direct.category);
        setDirectReason(sugestao.direct.reason);
        setSugAplicada(true);
      }
    }
    setStep(2);
  }
  function usarHistorico() {
    if (!sugestao?.direct) return;
    setDirectCategory(sugestao.direct.category);
    setDirectReason(sugestao.direct.reason);
    setSugAplicada(true);
  }

  // ── Seleção de faturas/recebimentos (uma ou várias) ────────────────────
  const recibosSoltos = receiptsQuery.data?.rows ?? [];
  const clientesDaLista = [
    ...new Set(
      [
        ...faturasAbertas.map((f) => f.cliente),
        ...recibosSoltos.map((r) => r.invoiceCustomerName),
      ].filter((n): n is string => !!n),
    ),
  ].sort();
  const filtroNum = filtroNumero.trim().toUpperCase().replace(/\s+/g, "");
  const alvosVisiveis = alvosRecebimento.filter((a) => {
    if (filtroCliente !== "all") {
      const cliente =
        a.kind === "invoice"
          ? faturasAbertas.find((f) => f.id === a.id)?.cliente
          : recibosSoltos.find((r) => r.id === a.id)?.invoiceCustomerName;
      if (cliente !== filtroCliente) return false;
    }
    if (filtroNum && !a.label.toUpperCase().replace(/\s+/g, "").includes(filtroNum)) return false;
    return true;
  });
  const filtroAtivo = filtroCliente !== "all" || filtroNum.length > 0;
  const toggleIn = (set: (f: (prev: Set<number>) => Set<number>) => void, id: number) =>
    set((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAlvo = (a: Alvo) =>
    a.kind === "receipt" ? toggleIn(setSelReceipts, a.id) : toggleIn(setSelInvoices, a.id);
  const marcarVisiveis = () => {
    setSelInvoices((prev) => {
      const n = new Set(prev);
      alvosVisiveis.filter((a) => a.kind === "invoice").forEach((a) => n.add(a.id));
      return n;
    });
    setSelReceipts((prev) => {
      const n = new Set(prev);
      alvosVisiveis.filter((a) => a.kind === "receipt").forEach((a) => n.add(a.id));
      return n;
    });
  };
  const limparSelecao = () => {
    setSelInvoices(new Set());
    setSelReceipts(new Set());
  };
  // Soma do que está marcado (TTC) e diferença para o depósito, ao vivo.
  const somaSel =
    faturasAbertas.filter((f) => selInvoices.has(f.id)).reduce((acc, f) => acc + f.ttc, 0) +
    recibosSoltos.filter((r) => selReceipts.has(r.id)).reduce((acc, r) => acc + rTtc(r), 0);
  const nSel = selInvoices.size + selReceipts.size;
  const multiSel = nSel > 1;
  const diffSel = Math.round((depositoAbs - somaSel) * 100) / 100;
  const somaPassa = multiSel && diffSel < -1;

  // CEO 2026-10-27 (MCB: motivo "481/460/461", valor = 483 + 460 + 461) —
  // procura, entre as faturas abertas do MESMO cliente, a combinação que
  // fecha o depósito exato; prefere a que usa mais faturas citadas no virement.
  const clienteAlvo = (() => {
    const unico = (ids: Array<number | null>) => {
      const set = new Set(ids.filter((x): x is number => x != null));
      return set.size === 1 ? [...set][0]! : null;
    };
    return (
      unico(faturasAbertas.filter((f) => f.rank === 0).map((f) => f.customerId)) ??
      unico(faturasAbertas.filter((f) => selInvoices.has(f.id)).map((f) => f.customerId))
    );
  })();
  const precisaCombo =
    credito &&
    clienteAlvo != null &&
    selReceipts.size === 0 &&
    (nSel === 0 || Math.abs(diffSel) > 1);
  const comboChave = precisaCombo
    ? `${clienteAlvo}|${depositoAbs}|${faturasAbertas
        .filter((f) => f.customerId === clienteAlvo)
        .map((f) => `${f.id}:${f.ttc}:${f.rank}`)
        .join(",")}`
    : "";
  const comboExato = useMemo(() => {
    if (!comboChave) return null;
    const pool = faturasAbertas
      .filter((f) => f.customerId === clienteAlvo && f.ttc > 0)
      .sort((a, b) => a.rank - b.rank || (b.date ?? "").localeCompare(a.date ?? ""))
      .slice(0, 18);
    const alvo = Math.round(depositoAbs * 100);
    type Soma = { mask: number; cit: number; n: number };
    let mapa = new Map<number, Soma>([[0, { mask: 0, cit: 0, n: 0 }]]);
    pool.forEach((f, i) => {
      const c = Math.round(f.ttc * 100);
      const novo = new Map(mapa);
      for (const [soma, m] of mapa) {
        const total = soma + c;
        if (total > alvo + 1) continue;
        const cand = { mask: m.mask | (1 << i), cit: m.cit + (f.rank === 0 ? 1 : 0), n: m.n + 1 };
        const atual = novo.get(total);
        if (!atual || cand.cit > atual.cit || (cand.cit === atual.cit && cand.n < atual.n)) {
          novo.set(total, cand);
        }
      }
      mapa = novo;
    });
    let melhor: Soma | null = null;
    for (const k of [alvo, alvo - 1, alvo + 1]) {
      const m = mapa.get(k);
      if (
        m &&
        m.n > 0 &&
        (!melhor || m.cit > melhor.cit || (m.cit === melhor.cit && m.n < melhor.n))
      ) {
        melhor = m;
      }
    }
    if (!melhor) return null;
    const mask = melhor.mask;
    return {
      ids: pool.filter((_, i) => (mask & (1 << i)) !== 0).map((f) => f.id),
      numeros: pool.filter((_, i) => (mask & (1 << i)) !== 0).map((f) => f.numero),
      foraCitadas: pool
        .filter((f, i) => f.rank === 0 && (mask & (1 << i)) === 0)
        .map((f) => f.numero),
    };
    // A chave resume tudo o que a busca usa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboChave]);
  const comboDiferente =
    comboExato != null &&
    (comboExato.ids.length !== selInvoices.size ||
      comboExato.ids.some((id) => !selInvoices.has(id)));

  // CEO 2026-10-27 — sobra do depósito vira crédito do cliente (faturas de um
  // cliente só); o texto do resumo diz para quem.
  const sobraCliente = (() => {
    const ids = new Set(
      faturasAbertas.filter((f) => selInvoices.has(f.id)).map((f) => f.customerId),
    );
    if (ids.size !== 1 || ids.has(null)) return null;
    const id = [...ids][0];
    return { nome: faturasAbertas.find((f) => f.customerId === id)?.cliente ?? "" };
  })();
  const textoSobra = (valor: number) =>
    `${tr("bankStatements.sumSurplusPre")} ${money(valor)}${
      selInvoices.size === 0
        ? "."
        : sobraCliente
          ? ` ${tr("customerCredit.surplusToCredit")} ${sobraCliente.nome}.`
          : ` (${tr("customerCredit.surplusNoCredit")}).`
    }`;
  const bannerCombo =
    comboDiferente && comboExato ? (
      <div
        className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs"
        style={{ background: "#E0F2FE", borderColor: "#0284C7", color: "#0C4A6E" }}
        data-testid="receipt-combo"
      >
        <span>
          {tr("customerCredit.comboPre")} <b>{comboExato.numeros.join(" + ")}</b>{" "}
          {tr("customerCredit.comboMid")} <b>{money(depositoAbs)}</b>.
          {comboExato.foraCitadas.length > 0 &&
            ` ${tr("customerCredit.comboCitedOut")} ${comboExato.foraCitadas.join(", ")}, ${tr("customerCredit.comboCitedOutSuf")}`}
        </span>
        <button
          type="button"
          onClick={() => {
            setSelInvoices(new Set(comboExato.ids));
            setSelReceipts(new Set());
          }}
          className="rounded-md border px-3 py-1 font-semibold"
          style={{ borderColor: "#0284C7", background: "#FFFFFF", color: "#0C4A6E" }}
          data-testid="receipt-combo-use"
        >
          {tr("customerCredit.comboUse")}
        </button>
      </div>
    ) : null;

  // ── Validade do passo 2 e objetos escolhidos ───────────────────────────
  const directNeedsReason = directCategory === "other" && directReason === "";
  const directNeedsDescription = directReason === "other";
  const directOk =
    !directNeedsReason && (!directNeedsDescription || directDescription.trim().length > 0);
  const podeContinuar =
    tipo === "receipt" ? nSel > 0 && !somaPassa : tipo === "document" ? selDoc != null : directOk;
  const faturaSel =
    selInvoices.size === 1 && selReceipts.size === 0
      ? (faturasAbertas.find((f) => selInvoices.has(f.id)) ?? null)
      : null;
  const reciboSel =
    selReceipts.size === 1 && selInvoices.size === 0
      ? (alvosRecebimento.find((a) => a.kind === "receipt" && selReceipts.has(a.id)) ?? null)
      : null;
  const docSel = selDoc != null ? (alvosDocumento.find((a) => a.id === selDoc) ?? null) : null;
  const diff = faturaSel ? Math.round((depositoAbs - faturaSel.ttc) * 100) / 100 : 0;
  const menor = faturaSel != null && diff < -1;
  const maior = faturaSel != null && diff > 1;
  const avoirNumQuery = trpc.customerCreditNotes.suggestNextNumber.useQuery(
    { companyId },
    { enabled: step === 3 && menor && parcial === "avoir" },
  );
  // Motivo padrão do avoir: o texto do virement (nº do avoir citado, data).
  const motivoPadraoAvoir =
    `${tr("bankStatements.avoirReasonDefault")} ${tx?.transactionDate ?? ""}${
      tx?.description ? ` · ${tx.description.slice(0, 160)}` : ""
    }`.slice(0, 500);
  const directReasons = bankDirectReasonsFor(directCategory);
  const categoriaLabel =
    CATEGORIAS.find((c) => c.value === directCategory)?.label ?? directCategory;

  const resumo: string = (() => {
    if (tipo === "receipt") {
      if (multiSel) {
        const numeros = [
          ...faturasAbertas.filter((f) => selInvoices.has(f.id)).map((f) => f.numero),
          ...recibosSoltos
            .filter((r) => selReceipts.has(r.id))
            .map((r) => `${tr("bankStatements.receiptWord")} #${r.id}`),
        ].join(", ");
        const sobra = diffSel > 1 ? ` ${textoSobra(diffSel)}` : "";
        return `${tr("bankStatements.sumDepositWord")} ${money(depositoAbs)} ${tr("bankStatements.sumPaysInvoices")} ${numeros} (Σ TTC ${money(somaSel)}), ${tr("bankStatements.sumMultiPaidSuf")}${sobra}`;
      }
      if (faturaSel) {
        const base = `${tr("bankStatements.sumDepositWord")} ${money(depositoAbs)} ${tr("bankStatements.sumPaysInvoice")} ${faturaSel.numero}${faturaSel.cliente ? ` · ${faturaSel.cliente}` : ""} (${money(faturaSel.ttc)} TTC)`;
        if (menor)
          return parcial === "partial"
            ? `${base} ${tr("bankStatements.sumInvoicePartial")} ${tr("bankStatements.remainingWord")} ${money(-diff)}.`
            : parcial === "avoir"
              ? `${base} — ${tr("bankStatements.sumAvoirPre")} ${money(-diff)} ${tr("bankStatements.sumAvoirMid")} ${avoirNumQuery.data ?? "A…"} ${tr("bankStatements.sumAvoirSuf")}`
              : `${base}, ${tr("bankStatements.sumInvoiceFullAnyway")}`;
        return `${base}, ${tr("bankStatements.sumInvoicePaid")}${maior ? ` ${textoSobra(diff)}` : ""}`;
      }
      if (reciboSel)
        return `${tr("bankStatements.sumDepositWord")} ${money(depositoAbs)} ${tr("bankStatements.sumLinksReceipt")} ${reciboSel.label}.`;
      return "";
    }
    if (tipo === "document") {
      if (!docSel) return "";
      return `${credito ? tr("bankStatements.sumDepositWord") : tr("bankStatements.sumOutflowWord")} ${money(depositoAbs)} ${tr("bankStatements.sumPaysDoc")} ${docSel.label} (${money(docSel.amount)}). ${docSel.detalhe ?? tr("bankStatements.sumDocPaid")}`;
    }
    const motivo = directReason
      ? bankDirectReasonLabel(directReason, locale)
      : tr("bankStatements.sumNoReason");
    return `${categoriaLabel} · ${motivo}${directDescription.trim() ? ` · ${directDescription.trim()}` : ""}${ehAdiant && !credito ? ` · ${tr("bankStatements.advSummaryTag")}` : ""}`;
  })();

  /**
   * F-avoir-na-conciliacao — a diferença (TTC) vira um avoir sobre a fatura
   * (HT pela TVA da fatura; autoliquidação = sem TVA) e, em seguida, a fatura
   * é dada por paga com o depósito. Dois passos no servidor; erro de qualquer
   * um aparece no painel.
   */
  async function confirmarComAvoir(f: NonNullable<typeof faturaSel>) {
    const numero = avoirNumQuery.data;
    if (!numero) {
      setErro(tr("bankStatements.avoirNumberMissing"));
      return;
    }
    const diffTtc = Math.round((f.ttc - depositoAbs) * 100) / 100;
    const ht = f.reverseCharge ? diffTtc : diffTtc / (1 + f.tvaRate / 100);
    try {
      await avoirMutation.mutateAsync({
        companyId,
        invoiceId: f.id,
        creditNoteNumber: numero,
        creditType: avoirTipo,
        totalHt: Math.round(ht * 100) / 100,
        tvaRate: f.reverseCharge ? 0 : f.tvaRate,
        reason: (avoirMotivo.trim() || motivoPadraoAvoir).slice(0, 500),
        issuedAt: String(tx?.transactionDate ?? new Date().toISOString().slice(0, 10)).slice(0, 10),
      });
      await reconcileInvoiceMutation.mutateAsync({
        companyId,
        transactionId,
        customerInvoiceId: f.id,
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  function confirmar() {
    setErro(null);
    if (tipo === "receipt") {
      if (multiSel) {
        const payload: {
          companyId: number;
          transactionId: number;
          customerInvoiceIds?: number[];
          customerReceiptIds?: number[];
        } = { companyId, transactionId };
        if (selInvoices.size > 0) payload.customerInvoiceIds = [...selInvoices];
        if (selReceipts.size > 0) payload.customerReceiptIds = [...selReceipts];
        reconcileMultiMutation.mutate(payload);
        return;
      }
      if (faturaSel) {
        if (menor && parcial === "avoir") {
          void confirmarComAvoir(faturaSel);
          return;
        }
        if (menor && parcial === "partial") {
          // Recibo é HT: tira a TVA do depósito (autoliquidação = sem TVA).
          const ht = faturaSel.reverseCharge
            ? depositoAbs
            : depositoAbs / (1 + faturaSel.tvaRate / 100);
          const htR = Math.round(ht * 100) / 100;
          if (faturaSel.multiCarro) {
            setAllocFor({ invoiceId: faturaSel.id, ht: htR });
            return;
          }
          reconcileInvoiceMutation.mutate({
            companyId,
            transactionId,
            customerInvoiceId: faturaSel.id,
            amount: htR,
          });
          return;
        }
        reconcileInvoiceMutation.mutate({
          companyId,
          transactionId,
          customerInvoiceId: faturaSel.id,
        });
        return;
      }
      if (reciboSel) {
        reconcileMutation.mutate({
          companyId,
          transactionId,
          kind: "customer_receipt",
          targetId: reciboSel.id,
        });
      }
      return;
    }
    if (tipo === "document") {
      if (selDoc != null) {
        reconcileMutation.mutate({ companyId, transactionId, kind: docKind, targetId: selDoc });
      }
      return;
    }
    const payload: {
      companyId: number;
      transactionId: number;
      category: CategoryValue;
      reason?: BankDirectReasonKey;
      description?: string;
    } = { companyId, transactionId, category: directCategory };
    if (directReason !== "") payload.reason = directReason;
    const trimmed = directDescription.trim();
    if (trimmed) payload.description = trimmed;
    if (ehAdiant && !credito) {
      const advances = advRows
        .filter((r) => r.technicianId !== "" && Number(r.amount.replace(",", ".")) > 0)
        .map((r) => ({
          technicianId: r.technicianId as number,
          amount: Math.round(Number(r.amount.replace(",", ".")) * 100) / 100,
          ...(r.description.trim() ? { description: r.description.trim() } : {}),
        }));
      if (advances.length === 0) {
        setErro(tr("bankStatements.advNoRows"));
        return;
      }
      const soma = advances.reduce((s, a) => s + a.amount, 0);
      if (soma > depositoAbs + 0.005) {
        setErro(tr("bankStatements.advSumExceeds"));
        return;
      }
      reconcileDirectMutation.mutate({ ...payload, advances });
      return;
    }
    reconcileDirectMutation.mutate(payload);
  }

  // F-pay-allocation — a escolha dos carros abre POR CIMA do painel.
  if (allocFor != null) {
    return (
      <PaymentAllocationModal
        companyId={companyId}
        invoiceId={allocFor.invoiceId}
        depositHt={allocFor.ht}
        onClose={() => setAllocFor(null)}
        onConfirm={(dossierIds) => {
          reconcileInvoiceMutation.mutate({
            companyId,
            transactionId,
            customerInvoiceId: allocFor.invoiceId,
            amount: allocFor.ht,
            allocateDossierIds: dossierIds,
          });
          setAllocFor(null);
        }}
      />
    );
  }

  const parsed = parseBankLabel(tx?.description ?? "");
  const quem = parsed.counterparty ?? bankLabelFallback(tx?.description);
  const PASSOS: ReadonlyArray<{ n: Passo; label: string }> = [
    { n: 1, label: tr("bankStatements.stepWhat") },
    { n: 2, label: tr("bankStatements.stepDetails") },
    { n: 3, label: tr("bankStatements.stepConfirm") },
  ];
  const OPCOES: ReadonlyArray<{ id: SheetType; titulo: string; hint: string }> = [
    {
      id: "receipt",
      titulo: tr("bankStatements.typeReceipt"),
      hint: tr("bankStatements.typeReceiptHint"),
    },
    {
      id: "document",
      titulo: tr("bankStatements.typeDocument"),
      hint: tr("bankStatements.typeDocumentHint"),
    },
    {
      id: "direct",
      titulo: tr("bankStatements.typeDirect"),
      hint: tr("bankStatements.typeDirectHint"),
    },
  ];

  const linhaAlvo = (a: Alvo, checked: boolean, onPick: () => void, multiplo = false) => (
    <label
      key={a.key}
      data-testid={`alvo-${a.key}`}
      className={`flex cursor-pointer items-start gap-3 px-3 py-2.5 text-sm transition-colors hover:bg-neutral-800/30 ${checked ? "bg-lime/10" : ""}`}
    >
      <input
        type={multiplo ? "checkbox" : "radio"}
        name={multiplo ? undefined : "alvo"}
        checked={checked}
        onChange={onPick}
        className="mt-1 h-4 w-4 accent-lime"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-neutral-100">{a.label}</span>
        {a.detalhe && <span className="block text-[11px] text-neutral-500">{a.detalhe}</span>}
        {a.hint && (
          <span className="mt-0.5 inline-block rounded bg-lime/15 px-1.5 py-0.5 text-[10px] font-semibold text-lime">
            {a.hint}
          </span>
        )}
      </span>
      <span className="flex-none text-right">
        <span className="block font-mono text-sm tabular-nums">{money(a.amount)}</span>
        {a.date && <span className="block text-[11px] text-neutral-500">{a.date}</span>}
      </span>
    </label>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={tr("bankStatements.reconcileTitle")}
        data-testid="reconcile-sheet"
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-neutral-800 bg-neutral-950 shadow-2xl sm:w-[640px]"
      >
        <header className="flex items-start gap-3 border-b border-neutral-800 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-neutral-100">
              {tr("bankStatements.reconcileTitle")}
            </h2>
            {tx && (
              <div className="mt-1.5 flex items-baseline gap-3">
                <div className="min-w-0 flex-1">
                  <div
                    className="truncate text-sm font-semibold text-neutral-100"
                    title={tx.description ?? ""}
                  >
                    {tx.transactionDate} · {quem}
                  </div>
                  {parsed.motif && (
                    <div className="truncate text-[11px] text-neutral-500">{parsed.motif}</div>
                  )}
                </div>
                <span
                  className={`flex-none font-mono text-lg font-bold tabular-nums ${credito ? "text-lime" : "text-red-400"}`}
                  data-testid="sheet-amount"
                >
                  {money(tx.amount)}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tr("bankStatements.sheetCloseLabel")}
            className="text-xl leading-none text-neutral-500 hover:text-neutral-200"
          >
            ×
          </button>
        </header>

        <ol className="grid grid-cols-3 gap-1 px-5 pt-3" data-testid="reconcile-steps">
          {PASSOS.map((p) => {
            const feito = p.n < step;
            const atual = p.n === step;
            const podeIr =
              p.n < step ||
              (p.n === 2 && tipo != null) ||
              (p.n === 3 && tipo != null && podeContinuar);
            return (
              <li key={p.n}>
                <button
                  type="button"
                  data-testid={`step-${p.n}`}
                  aria-current={atual ? "step" : undefined}
                  disabled={!podeIr && !atual}
                  onClick={() => setStep(p.n)}
                  className={`flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors disabled:cursor-default ${
                    atual
                      ? "border-lime bg-lime/15 font-semibold text-neutral-100"
                      : feito
                        ? "border-lime/40 text-neutral-300 hover:border-lime"
                        : "border-neutral-800 text-neutral-500"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-bold ${
                      atual || feito ? "bg-lime text-on-lime" : "bg-neutral-800 text-neutral-400"
                    }`}
                  >
                    {feito ? "✓" : p.n}
                  </span>
                  <span className="truncate">{p.label}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* ── Passo 1: o que é? ─────────────────────────────────────── */}
          {step === 1 && (
            <div className="grid gap-2 sm:grid-cols-3" data-testid="sheet-types">
              {OPCOES.map((o) => {
                const sugerida = sugestao?.tipo === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`type-${o.id}`}
                    data-sugestao={sugerida ? "true" : undefined}
                    onClick={() => escolherTipo(o.id)}
                    className={`flex flex-col items-start gap-1 rounded-xl border-2 p-3.5 text-left transition-colors hover:border-lime ${
                      sugerida ? "border-lime bg-lime/10" : "border-neutral-800 bg-neutral-900"
                    }`}
                  >
                    <span className="text-[15px] font-semibold text-neutral-100">{o.titulo}</span>
                    <span className="text-[11.5px] leading-snug text-neutral-500">{o.hint}</span>
                    {sugerida && sugestao && (
                      <span
                        className="mt-1 text-[11px] leading-snug text-lime"
                        data-testid="sug-badge"
                      >
                        <span className="rounded bg-lime/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">
                          {tr("bankStatements.sugBadge")}
                        </span>{" "}
                        {sugestao.texto}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Passo 2: recebimento de cliente ───────────────────────── */}
          {step === 2 && tipo === "receipt" && (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-neutral-100">
                  {tr("bankStatements.receiptPickTitle")}
                </h3>
                <span className="text-xs text-neutral-400">
                  {tr("bankStatements.receiptPickHint")}
                </span>
              </div>
              {/* Soma ao vivo × depósito (CEO 2026-10-25) — no TOPO e com fundo
                  sólido: embaixo da lista ficava escondido e, no tema claro, o
                  âmbar translúcido sumia no fundo. */}
              {(() => {
                const estado =
                  nSel === 0
                    ? "vazio"
                    : somaPassa
                      ? "passa"
                      : Math.abs(diffSel) <= 1
                        ? "bate"
                        : "sobra";
                const estilo: Record<
                  string,
                  { background: string; color: string; border: string }
                > = {
                  vazio: {
                    background: "var(--surface-muted)",
                    color: "var(--text-body)",
                    border: "var(--border)",
                  },
                  bate: { background: "#a4e05a", color: "#0f172a", border: "#7cb342" },
                  sobra: { background: "#f59e0b", color: "#1c1917", border: "#b45309" },
                  passa: { background: "#dc2626", color: "#ffffff", border: "#991b1b" },
                };
                const e = estilo[estado]!;
                return (
                  <div
                    className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg px-4 py-2.5 text-sm font-medium"
                    style={{
                      background: e.background,
                      color: e.color,
                      border: `1px solid ${e.border}`,
                    }}
                    data-testid="receipt-sum"
                    data-estado={estado}
                  >
                    <span>
                      {nSel} {tr("bankStatements.selectedCount")} · Σ TTC{" "}
                      <b className="font-mono text-base tabular-nums">{money(somaSel)}</b>
                    </span>
                    <span>
                      {tr("bankStatements.depositLabel")}{" "}
                      <b className="font-mono text-base tabular-nums">{money(depositoAbs)}</b>
                    </span>
                    {nSel > 0 && (
                      <span className="w-full font-bold sm:w-auto">
                        {Math.abs(diffSel) <= 1
                          ? `✓ ${tr("bankStatements.sumMatches")}`
                          : diffSel > 1
                            ? `${tr("bankStatements.sumSurplusPre")} ${money(diffSel)}`
                            : multiSel
                              ? `${tr("bankStatements.sumOverPre")} ${money(-diffSel)}`
                              : `${tr("bankStatements.diffWord")} ${money(diffSel)} · ${tr("bankStatements.sumPartialHint")}`}
                      </span>
                    )}
                  </div>
                );
              })()}
              {somaPassa && (
                <p
                  className="rounded-md px-3 py-2 text-xs font-medium"
                  style={{ background: "#fef3c7", color: "#78350f", border: "1px solid #f59e0b" }}
                >
                  {tr("bankStatements.multiShortfall")}
                </p>
              )}
              {bannerCombo}
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={filtroCliente}
                  onChange={(e) => setFiltroCliente(e.target.value)}
                  className={bankInputClass + " w-48"}
                  title={tr("bankStatements.filterInvoicesByCustomerTitle")}
                  data-testid="receipt-filter-customer"
                >
                  <option value="all">{tr("bankStatements.allCustomers")}</option>
                  {clientesDaLista.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <input
                  value={filtroNumero}
                  onChange={(e) => setFiltroNumero(e.target.value)}
                  placeholder={tr("bankStatements.receiptFilterNumber")}
                  className={bankInputClass + " w-40"}
                  data-testid="receipt-filter-number"
                />
                {filtroAtivo && alvosVisiveis.length > 0 && (
                  <button
                    type="button"
                    onClick={marcarVisiveis}
                    className="rounded-md border border-lime/50 px-2.5 py-1 text-xs font-semibold text-lime hover:bg-lime/10"
                    data-testid="receipt-select-visible"
                  >
                    {tr("bankStatements.selectVisible")} ({alvosVisiveis.length})
                  </button>
                )}
                {nSel > 0 && (
                  <button
                    type="button"
                    onClick={limparSelecao}
                    className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs text-neutral-300 hover:border-neutral-500"
                    data-testid="receipt-clear"
                  >
                    {tr("bankStatements.clearSelection")} ({nSel})
                  </button>
                )}
              </div>
              <div className="max-h-[48vh] divide-y divide-neutral-800 overflow-y-auto rounded-md border border-neutral-800">
                {alvosVisiveis.length === 0 && (
                  <p className="px-3 py-4 text-center text-sm text-neutral-500">
                    {openInvQuery.isLoading || receiptsQuery.isLoading
                      ? tr("bankStatements.loading")
                      : tr("bankStatements.noCandidates")}
                  </p>
                )}
                {alvosVisiveis.map((a) => {
                  const marcado =
                    a.kind === "receipt" ? selReceipts.has(a.id) : selInvoices.has(a.id);
                  // Depósito maior que o marcado: aponta a fatura cujo saldo fecha a sobra.
                  const fechaSobra =
                    !marcado &&
                    nSel > 0 &&
                    diffSel > 1 &&
                    Math.abs(Number(a.amount) - diffSel) <= 1;
                  return linhaAlvo(
                    fechaSobra ? { ...a, hint: a.hint ?? tr("bankStatements.matchSurplus") } : a,
                    marcado,
                    () => toggleAlvo(a),
                    true,
                  );
                })}
              </div>
            </>
          )}

          {/* ── Passo 2: pagamento de um documento ────────────────────── */}
          {step === 2 && tipo === "document" && (
            <>
              <h3 className="text-sm font-semibold text-neutral-100">
                {tr("bankStatements.docKindTitle")}
              </h3>
              <div className="flex flex-wrap gap-1.5" data-testid="doc-kinds">
                {DOC_KINDS.map((k) => {
                  const on = docKind === k.value;
                  return (
                    <button
                      key={k.value}
                      type="button"
                      data-testid={`doc-kind-${k.value}`}
                      aria-pressed={on}
                      onClick={() => {
                        setDocKind(k.value);
                        setSelDoc(null);
                        setNovaFatura(false);
                      }}
                      className={`inline-flex h-8 items-center rounded-full border px-3 text-xs font-semibold transition-colors ${
                        on
                          ? "border-lime bg-lime text-on-lime"
                          : "border-neutral-700 bg-neutral-900 text-neutral-300 hover:border-lime hover:text-lime"
                      }`}
                    >
                      {k.label}
                    </button>
                  );
                })}
              </div>
              <h3 className="text-sm font-semibold text-neutral-100">
                {tr("bankStatements.docPickTitle")}
              </h3>
              <div className="divide-y divide-neutral-800 overflow-hidden rounded-md border border-neutral-800">
                {alvosDocumento.length === 0 && (
                  <p className="px-3 py-4 text-center text-sm text-neutral-500">
                    {tr("bankStatements.noCandidates")}
                  </p>
                )}
                {alvosDocumento.map((a) => linhaAlvo(a, selDoc === a.id, () => setSelDoc(a.id)))}
              </div>
              {docKind === "purchase_invoice" && tx && (
                <div>
                  <button
                    type="button"
                    data-testid="doc-register-new"
                    onClick={() => setNovaFatura((v) => !v)}
                    className="text-xs text-lime underline-offset-2 hover:underline"
                  >
                    {tr("bankStatements.docRegisterNew")}
                  </button>
                  {novaFatura && (
                    <div className="mt-2">
                      <NewPurchaseInvoiceForm
                        companyId={companyId}
                        transactionId={transactionId}
                        depositAmount={depositoAbs}
                        onSaved={onSaved}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Passo 2: sem documento ────────────────────────────────── */}
          {step === 2 && tipo === "direct" && (
            <>
              {sugestao?.direct && (
                <div
                  className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${
                    sugAplicada
                      ? "border-lime/40 bg-lime/10 text-neutral-300"
                      : "border-neutral-700 bg-neutral-900 text-neutral-300"
                  }`}
                  data-testid="sug-history"
                >
                  <span className="rounded bg-lime/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-lime">
                    {tr("bankStatements.sugBadge")}
                  </span>
                  <span className="min-w-0 flex-1">{sugestao.texto}</span>
                  {sugAplicada ? (
                    <span className="font-semibold text-lime">
                      ✓ {tr("bankStatements.sugApplied")}
                    </span>
                  ) : (
                    <button
                      type="button"
                      data-testid="sug-use-same"
                      onClick={usarHistorico}
                      className="rounded-md border border-lime/50 px-2 py-0.5 font-semibold text-lime hover:bg-lime/10"
                    >
                      {tr("bankStatements.sugUseSame")}
                    </button>
                  )}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-neutral-400">
                    {tr("bankStatements.directCategoryLabel")}
                  </label>
                  <select
                    value={directCategory}
                    data-testid="direct-category"
                    onChange={(e) => {
                      const v = e.target.value as CategoryValue;
                      setDirectCategory(v);
                      setSugAplicada(false);
                      // motivo mantido só se continua servindo para a categoria nova
                      setDirectReason((r) => {
                        if (r === "") return "";
                        const { suggested } = bankDirectReasonsFor(v);
                        return suggested.some((x) => x.key === r) ? r : "";
                      });
                    }}
                    className={`${bankInputClass} w-full`}
                  >
                    {CATEGORIAS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-neutral-400">
                    {tr("bankStatements.directReasonLabel")}
                    {directCategory === "other" && <span className="text-red-400"> *</span>}
                  </label>
                  <select
                    value={directReason}
                    onChange={(e) => {
                      setDirectReason(e.target.value as BankDirectReasonKey | "");
                      setSugAplicada(false);
                    }}
                    className={`${bankInputClass} w-full`}
                    data-testid="direct-reason"
                  >
                    <option value="">{tr("bankStatements.directReasonNone")}</option>
                    {directReasons.suggested.length > 0 && (
                      <optgroup label={tr("bankStatements.directReasonSuggested")}>
                        {directReasons.suggested.map((r) => (
                          <option key={r.key} value={r.key}>
                            {bankDirectReasonLabel(r.key, locale)}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label={tr("bankStatements.directReasonOthers")}>
                      {directReasons.others.map((r) => (
                        <option key={r.key} value={r.key}>
                          {bankDirectReasonLabel(r.key, locale)}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="mt-1 text-[11px] text-neutral-500">
                    {directNeedsReason
                      ? tr("bankStatements.directReasonRequiredOther")
                      : tr("bankStatements.directReasonHint")}
                  </p>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-neutral-400">
                  {tr("bankStatements.directComplementLabel")}{" "}
                  {directNeedsDescription ? (
                    <span className="text-red-400">*</span>
                  ) : (
                    <span className="text-neutral-600">{tr("bankStatements.optionalSuffix")}</span>
                  )}
                </label>
                <input
                  type="text"
                  value={directDescription}
                  onChange={(e) => setDirectDescription(e.target.value)}
                  placeholder={tr("bankStatements.directComplementHint")}
                  maxLength={500}
                  className={`${bankInputClass} w-full`}
                  data-testid="direct-complement"
                />
                {directNeedsDescription && directDescription.trim().length === 0 && (
                  <p className="mt-1 text-[11px] text-amber-300">
                    {tr("bankStatements.directComplementRequired")}
                  </p>
                )}
              </div>

              {/* F-adiantamento-tecnico — só em SAÍDA: a despesa é adiantamento a
                  técnico(s); cada parte vira um adiantamento aberto. */}
              {!credito && (
                <div
                  className={`rounded-md border px-3 py-2 ${ehAdiant ? "border-amber-500/40 bg-amber-500/10" : "border-neutral-800"}`}
                  data-testid="adv-block"
                >
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={ehAdiant}
                      onChange={(e) => setEhAdiant(e.target.checked)}
                      data-testid="adv-toggle"
                    />
                    <span className="font-medium">{tr("bankStatements.advToggle")}</span>
                  </label>
                  <p className="mt-0.5 text-[11px] text-neutral-500">
                    {tr("bankStatements.advToggleHint")}
                  </p>
                  {ehAdiant && (
                    <div className="mt-2 space-y-2" data-testid="adv-rows">
                      {advRows.map((r, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2">
                          <select
                            value={r.technicianId}
                            onChange={(e) =>
                              setAdvRows((rows) =>
                                rows.map((x, j) =>
                                  j === i
                                    ? {
                                        ...x,
                                        technicianId:
                                          e.target.value === "" ? "" : Number(e.target.value),
                                      }
                                    : x,
                                ),
                              )
                            }
                            className={`${bankInputClass} min-w-[11rem] flex-1`}
                            data-testid={`adv-tech-${i}`}
                          >
                            <option value="">{tr("bankStatements.advPickTech")}</option>
                            {(techsQuery.data?.rows ?? []).map((tech) => (
                              <option key={tech.id} value={tech.id}>
                                {tech.name}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={r.amount}
                            onChange={(e) =>
                              setAdvRows((rows) =>
                                rows.map((x, j) =>
                                  j === i ? { ...x, amount: e.target.value } : x,
                                ),
                              )
                            }
                            placeholder="0,00"
                            className={`${bankInputClass} w-28 text-right font-mono`}
                            data-testid={`adv-amount-${i}`}
                          />
                          <input
                            type="text"
                            value={r.description}
                            onChange={(e) =>
                              setAdvRows((rows) =>
                                rows.map((x, j) =>
                                  j === i ? { ...x, description: e.target.value } : x,
                                ),
                              )
                            }
                            placeholder={tr("bankStatements.advDescHint")}
                            maxLength={300}
                            className={`${bankInputClass} min-w-[10rem] flex-[2]`}
                            data-testid={`adv-desc-${i}`}
                          />
                          {advRows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setAdvRows((rows) => rows.filter((_, j) => j !== i))}
                              className="text-xs text-neutral-500 hover:text-red-400"
                              aria-label={tr("bankStatements.advRemoveRow")}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      {(() => {
                        const soma = advRows.reduce(
                          (s, r) => s + (Number(r.amount.replace(",", ".")) || 0),
                          0,
                        );
                        const resto = depositoAbs - soma;
                        return (
                          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                            <div className="flex items-center gap-3">
                              {advRows.length < 10 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setAdvRows((rows) => [
                                      ...rows,
                                      {
                                        technicianId: "",
                                        amount: resto > 0.005 ? resto.toFixed(2) : "",
                                        description: "",
                                      },
                                    ])
                                  }
                                  className="text-neutral-300 hover:underline"
                                  data-testid="adv-add-row"
                                >
                                  + {tr("bankStatements.advAddRow")}
                                </button>
                              )}
                              {advRows.length === 1 && soma === 0 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setAdvRows((rows) =>
                                      rows.map((x, j) =>
                                        j === 0 ? { ...x, amount: depositoAbs.toFixed(2) } : x,
                                      ),
                                    )
                                  }
                                  className="text-neutral-300 hover:underline"
                                  data-testid="adv-fill-all"
                                >
                                  {tr("bankStatements.advFillAll")}
                                </button>
                              )}
                            </div>
                            <span
                              className="font-mono tabular-nums"
                              style={{
                                color:
                                  soma > depositoAbs + 0.005
                                    ? "#dc2626"
                                    : Math.abs(resto) < 0.005
                                      ? "#16a34a"
                                      : "#b45309",
                              }}
                              data-testid="adv-sum"
                            >
                              {money(soma)} / {money(depositoAbs)}
                              {Math.abs(resto) >= 0.005 && soma <= depositoAbs + 0.005
                                ? ` · ${tr("bankStatements.advRestNotAdvance")} ${money(resto)}`
                                : ""}
                              {soma > depositoAbs + 0.005
                                ? ` · ${tr("bankStatements.advSumExceeds")}`
                                : ""}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Passo 3: confirmar ────────────────────────────────────── */}
          {step === 3 && (
            <>
              {menor && (
                <fieldset className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                  <legend className="px-1 text-xs font-semibold text-amber-300">
                    {tr("bankStatements.depositLowerTitle")}
                  </legend>
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="parcial"
                      checked={parcial === "partial"}
                      onChange={() => setParcial("partial")}
                      data-testid="partial-option"
                      className="mt-1"
                    />
                    <span>
                      <span className="font-semibold text-neutral-100">
                        {tr("bankStatements.partialPaymentBtn")}
                      </span>
                      <span className="block text-xs text-neutral-400">
                        {tr("bankStatements.partialOptionHint")}
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="parcial"
                      checked={parcial === "full"}
                      onChange={() => setParcial("full")}
                      data-testid="full-option"
                      className="mt-1"
                    />
                    <span>
                      <span className="font-semibold text-neutral-100">
                        {tr("bankStatements.fullPaymentBtn")}
                      </span>
                      <span className="block text-xs text-neutral-400">
                        {tr("bankStatements.fullOptionHint")}
                      </span>
                    </span>
                  </label>
                  {isAdmin && (
                    <label className="flex cursor-pointer items-start gap-2 text-sm">
                      <input
                        type="radio"
                        name="parcial"
                        checked={parcial === "avoir"}
                        onChange={() => setParcial("avoir")}
                        data-testid="avoir-option"
                        className="mt-1"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold text-neutral-100">
                          {tr("bankStatements.avoirOption")}
                        </span>
                        <span className="block text-xs text-neutral-400">
                          {tr("bankStatements.avoirOptionHint")} {money(-diff)}
                        </span>
                        {parcial === "avoir" && (
                          <span className="mt-2 grid gap-2 sm:grid-cols-[10rem_1fr]">
                            <select
                              value={avoirTipo}
                              onChange={(e) =>
                                setAvoirTipo(e.target.value as "commercial" | "return" | "error")
                              }
                              className={`${bankInputClass} w-full`}
                              aria-label={tr("bankStatements.avoirTypeLabel")}
                              data-testid="avoir-type"
                            >
                              <option value="commercial">
                                {tr("invoices.creditTypeCommercial")}
                              </option>
                              <option value="return">{tr("invoices.creditTypeReturn")}</option>
                              <option value="error">{tr("invoices.creditTypeError")}</option>
                            </select>
                            <input
                              value={avoirMotivo}
                              onChange={(e) => setAvoirMotivo(e.target.value)}
                              placeholder={motivoPadraoAvoir}
                              maxLength={500}
                              className={`${bankInputClass} w-full`}
                              aria-label={tr("bankStatements.avoirReasonLabel")}
                              data-testid="avoir-reason"
                            />
                          </span>
                        )}
                      </span>
                    </label>
                  )}
                </fieldset>
              )}
              {tipo === "receipt" && bannerCombo}
              {maior && (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                  {tr("bankStatements.higherNote")}
                </p>
              )}
              <div
                className="rounded-lg border border-lime/40 bg-lime/10 px-4 py-3 text-sm text-neutral-100"
                data-testid="sheet-summary"
              >
                <span className="font-semibold">{tr("bankStatements.summaryPre")}</span> {resumo}
                {tipo === "direct" && (
                  <span className="mt-1 block text-[11px] text-neutral-500">
                    {tr("bankStatements.sumDirectNote")}
                  </span>
                )}
              </div>
              {erro && (
                <p
                  className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
                  data-testid="sheet-error"
                >
                  {erro}
                </p>
              )}
            </>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-neutral-800 px-5 py-3">
          {step === 1 ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-800 px-4 py-1.5 text-sm hover:border-neutral-600"
            >
              {tr("bankStatements.close")}
            </button>
          ) : (
            <button
              type="button"
              data-testid="sheet-back"
              onClick={() => {
                setErro(null);
                setStep(step === 3 ? 2 : 1);
              }}
              className="rounded-md border border-neutral-800 px-4 py-1.5 text-sm hover:border-neutral-600"
            >
              {tr("bankStatements.backBtn")}
            </button>
          )}
          {step === 2 && (
            <span className="flex items-center gap-3">
              {!podeContinuar && !(tipo === "receipt" && nSel > 0) && (
                <span className="hidden text-[11px] text-neutral-500 sm:inline">
                  {tr("bankStatements.pickOneToContinue")}
                </span>
              )}
              <button
                type="button"
                data-testid="sheet-next"
                disabled={!podeContinuar}
                onClick={() => setStep(3)}
                className="rounded-md bg-lime px-4 py-1.5 text-sm font-semibold text-on-lime hover:bg-lime-bright disabled:cursor-not-allowed disabled:opacity-40"
              >
                {tr("bankStatements.continueBtn")} →
              </button>
            </span>
          )}
          {step === 3 && (
            <button
              type="button"
              data-testid="sheet-confirm"
              disabled={pending || !podeContinuar}
              onClick={confirmar}
              className="rounded-md bg-lime px-4 py-1.5 text-sm font-semibold text-on-lime hover:bg-lime-bright disabled:cursor-not-allowed disabled:opacity-40"
            >
              {pending ? tr("bankStatements.confirming") : tr("bankStatements.confirm")}
            </button>
          )}
        </footer>
      </aside>
    </>
  );
}
